/*
Request row sync: per-field, versioned saves of request steps and actions.

XOL-596 (SPA half of XOL-594, support 0000984), against XOL-595 contract v3.
Plan of record: docs/plans/2026-10-06-xol-596-stale-workflow-saves.md (rev 3).

The incident: a stale drawer's whole-row save erased another user's approval and
completion. The design that stops it:

  * Workflow state (approved*, completed*, currentStep, status) is server-owned
    and changes only through commands. Nothing in this module ever sends it.
  * A content save is ONE path per request: {contentVersion, fields: {path: v},
    base: {path: n}}. A conflict is per field (409 stale_field); different
    fields never conflict.
  * Every field has its OWN base version (G-BASE). `base[path]` is the
    contentVersion of the response the field's displayed value came from, never
    the row's newest version. Sending the newest one after adopting a row while
    the field was being edited silently brings the lost update back.
  * Adopting a server row never overwrites a field the user is editing (R2.7).
    The server value is parked in `deferred` and applied when the field is
    released. Adoption merges by key and never replaces the row object or the
    steps array (G-MERGE), because save and command responses are lean.
  * Saves wait for a 3 s form-level pause with no input (R2.23). Only fields
    whose value differs from their base value are sent.
  * DECISION FIELDS (XOL-596, Carlos 2026-10-14): the approval controls (step 2's
    status and date, step 3's legal tick, step 5's status and date) save like every
    other field, at the pause. Their paths are server-owned, so the transport is the
    server's approval command (one per GROUP of paths), sent through the row's
    `decisionSender`. A group is held as a unit; its command's `expected...` value
    is the field's BASE (G-DECIDE), never the baseline.

This is a leaf module: it imports `appTime` and `session` only, never axios, a
mixin or a .vue file. The HTTP lives in RequestMixins (`sendFieldSave`), which
registers itself as each row's `sender`. Babel 6: no optional chaining or nullish coalescing.

Public surface (see each function's docblock):

  constants    PAUSE_MS, STEP_/ACTION_CONTENT_KEYS, STEP_/ACTION_OWNED_KEYS,
               ACTION_NONWORKFLOW_CONTENT_KEYS, STEP_DERIVED_KEYS,
               WORKFLOW_ACTION_TYPES, SERVER_OWNED_PATHS, STATE_OWNED_PATHS,
               LIST_PATHS
  paths        splitPath, isValidPath, getPath, setPath
  values       cloneValue, valuesEqual, normaliseEquipment
  ownership    checkWritablePath, checkPathOwnership           (G-OWN)
  leaves       changedLeafPaths, fieldsChangedUnder            (XOL-595 A4)
  rows         registerRow, getRecord, findRecord, recordFor, releaseRow
  fields       holdField, releaseField, fieldChanged, fieldCue, fieldDirty,
               fieldHeld, fieldFocused, takeServerValue, holdPaths, releasePaths,
               revertField, revertRow, restoreServerValue, leafPaths, takeServer
  body         buildFieldBody, confirmSaved                    (G-BASE)
  adopt        adoptRow, nextSeq, isOutdated, noteAdopted      (G-MERGE)
  baseline     getBaseline, baselineValue, holdOwned, releaseOwned,  (XOL-596 slice 3: commands)
               commandSent, commandAnswered                    (the workflow floor)
  decisions    registerDecision, decisionDay,                  (XOL-596: decision fields)
               decisionBase
  queue        enqueue, enqueueAll, whenIdle
  scopes       createScope, destroyScope, restartPause, cancelPause,
               attachScopeRoot, detachScopeRoot
  flushing     flushScope, flushRows, flushPaths, flushAllAndWait,
               hasPendingSaves, discardUnsaved, REASON_KEEP_TRYING
  bursts       setBurstReporter, summariseBurst
  lists        recordListOp, getListOps, pendingFields, releaseFocusedInScope
  meta         rowMeta, stepNoOfPath, stepNoOfOutcome          (XOL-596: completed steps)
  stash        stashLost, getStash, clearStash
  428          syncState (reactive), promptReload, setReloadPrompter
  reset        resetSyncState (registered with session.registerResetHook)
------------------------------------------------------------------------ */

import Vue from 'vue'
import { toAppDateTimeSql, toAppTime, DATE_SQL_FORMAT } from '@/lib/appTime'
import { registerResetHook, isTearingDown } from '@/lib/session'
import * as savePopup from '@/lib/savePopup'

/* ------------------------------------------------------------------------
   Constants and the ownership tables (plan F2, contract v3 §A)
------------------------------------------------------------------------ */

// The form-level pause (R2.23).
export var PAUSE_MS = 3000

// The `reason` of the flushes that keep retrying a blocked close / navigation during
// a connection problem (XOL-596, Carlos 2026-10-08). The burst reporter raises no
// second "connection problem" notice for them (the first one is still open).
export var REASON_KEEP_TRYING = 'keep-trying'

// Content columns: the only top-level keys a v3 `fields` path may start with.
export var STEP_CONTENT_KEYS = ['remarks', 'state', 'status', 'priority']
export var ACTION_CONTENT_KEYS = ['remarks', 'state', 'emails', 'summary', 'priority', 'targetDate', 'actualDate', 'actionBy']

// `status` is content on a NON-workflow action only; on a workflow type the
// reconciler owns it (contract v3 §A).
export var WORKFLOW_ACTION_TYPES = ['RTS', 'RTU', 'RTB', 'RTD', 'PRG']
export var ACTION_NONWORKFLOW_CONTENT_KEYS = ['status']

// Server-owned: change only through commands. Never in a body.
// XOL-596 (approval lock, XOL-595 plan 4.9): `approvalLocked` / `_by` / `_at` change
// only through POST /step/{id}/lock | unlock and `complete {lock: true}`, so a
// command's response decides them (the workflow floor) and a content save never
// carries them.
export var STEP_OWNED_KEYS = ['approved', 'approvedStatus', 'approved_at', 'approved_by', 'decided_at', 'approvalLocked', 'approvalLocked_by', 'approvalLocked_at', 'completed', 'completed_at', 'completed_by']
export var ACTION_OWNED_KEYS = ['currentStep', 'status', 'approved', 'approvalType', 'approved_at', 'approved_by', 'completed', 'completed_at', 'completed_by']

// XOL-586: keys of a step row the server DERIVES and returns, and the SPA only reads. They
// are not fields: they are adopted from a response like any other key (the merge is by key),
// have no entry and no base version, and are never in a body. `approvalMail` says whether the
// e-mail of this step's current approval content (status, date, remarks, ...) has been sent
// and to how many, or null once that content changed. Tower Co callers only.
export var STEP_DERIVED_KEYS = ['approvalMail']

// Server-owned PATHS inside content columns, and everything beneath them.
// `state.step3.legalApproved` is mirrored from the step-3 approval;
// `state.edits.*` are the server-written edit stamps (R2.21).
export var SERVER_OWNED_PATHS = ['state.step3.legalApproved', 'state.edits']

// Arrays are whole values at their own path: the API refuses a path beneath one
// (`emails.owner.additional.0`, `state.step2.equipment.x`) with a 422.
// XOL-586: the Emails Update lists are six, one per party and list; the legacy
// per-party arrays (the old `tower` and `telco` keys) are never written by this bundle.
export var LIST_PATHS = [
  'emails.towerco.additional', 'emails.towerco.unsubscribed',
  'emails.owner.additional', 'emails.owner.unsubscribed',
  'emails.provider.additional', 'emails.provider.unsubscribed',
  'state.step2.equipment'
]

// The only writable paths of the `emails` column (XOL-586, plan §3.6): the switch and
// the six lists. Anything else under `emails` (the whole column, a party object, the
// legacy keys) the API answers with a 422 read_only_fields.
var EMAILS_WRITABLE_PATHS = ['emails.sendMail'].concat(LIST_PATHS.filter(function (p) { return p.indexOf('emails.') === 0 }))

// Legacy shape kept for the lenient comparison (plan F2): segments, relative to
// the `state` column.
export var STATE_OWNED_PATHS = [['step3', 'legalApproved'], ['edits']]

// The statuses of a field-save outcome that count as "everything saved".
var OK_STATUSES = ['saved', 'skipped', 'teardown']

/* ------------------------------------------------------------------------
   Module state. Everything here is cleared by resetSyncState().
------------------------------------------------------------------------ */

var recordsByRow = new WeakMap()
var allRecords = []
var scopes = []
var queues = {}
var stash = []
var stashCounter = 0
var seqCounter = 0
var scopeCounter = 0
var burstCounter = 0
var burstReporter = null
// Set while the user has chosen to leave (Log out anyway / Leave now): the pending
// saves are given up, so no other leave guard of the SAME navigation holds it again.
// { to: fullPath } or null.
var leaveBypass = null
var reloadPrompter = null

/**
 * Reactive state for components.
 *
 *  - `versionRequiredSeen`: a 428 `version_required` was seen on this page load
 *    (the bundle is out of date). The C-8 banner reads it.
 *  - `activity`: {pending, saving, savingScoped, unsaved, pauseStartedAt, pauseMs}
 *    for the C-12 save-status popup. `pending` = a field changed and waiting for
 *    the pause (nothing of it in flight); `saving` = any save in flight or queued,
 *    also of a row outside every scope (an EditPane Save), `savingScoped` = the
 *    same for rows inside a drawer's scope; `unsaved` = fields whose save failed
 *    on the network (still dirty); `pauseStartedAt` = when the form pause last
 *    restarted (latest pending scope; 0 if none).
 *  - `tick`: bumped on every change to a field entry, cue or burst. The helpers
 *    components call from a render function (`fieldCue`, `fieldDirty`,
 *    `fieldHeld`) read it, so they re-render when the registry changes; the
 *    registry itself is plain objects.
 */
export var syncState = Vue.observable({
  versionRequiredSeen: false,
  tick: 0,
  // XOL-596 C-12: what the save-status popup shows, aggregated over every
  // registered record that is not an open EditPane draft (see refreshActivity).
  activity: {
    pending: false,
    saving: false,
    savingScoped: false,
    unsaved: 0,
    pauseStartedAt: 0,
    pauseMs: PAUSE_MS
  }
})

function bump () {
  syncState.tick++
}

// Reading `tick` inside a render function subscribes it to registry changes.
function track () {
  return syncState.tick
}

/* ------------------------------------------------------------------------
   Paths (R2.6)
------------------------------------------------------------------------ */

var SEGMENT = /^[A-Za-z0-9_]+$/
var NUMERIC = /^[0-9]+$/

/**
 * Split a dotted path into segments, or null when it is not a valid v3 path:
 * every segment must match [A-Za-z0-9_]+, and a numeric segment (an array
 * index) is refused, because arrays are whole values at their own path.
 */
export function splitPath (path) {
  if (typeof path !== 'string' || path === '') {
    return null
  }
  var parts = path.split('.')
  for (var i = 0; i < parts.length; i++) {
    if (!SEGMENT.test(parts[i]) || NUMERIC.test(parts[i])) {
      return null
    }
  }
  return parts
}

export function isValidPath (path) {
  return splitPath(path) !== null
}

function isObjectLike (v) {
  return v !== null && typeof v === 'object'
}

/** Read a dotted path. Undefined when any segment is missing. */
export function getPath (obj, path) {
  var parts = splitPath(path)
  if (!parts) {
    return undefined
  }
  var cur = obj
  for (var i = 0; i < parts.length; i++) {
    if (!isObjectLike(cur)) {
      return undefined
    }
    cur = cur[parts[i]]
  }
  return cur
}

/** True when every segment of the path exists on the object (even if null). */
function hasPath (obj, path) {
  var parts = splitPath(path)
  if (!parts) {
    return false
  }
  var cur = obj
  for (var i = 0; i < parts.length; i++) {
    if (!isObjectLike(cur) || !Object.prototype.hasOwnProperty.call(cur, parts[i])) {
      return false
    }
    cur = cur[parts[i]]
  }
  return true
}

/**
 * Write a dotted path through Vue.set, so a leaf that did not exist yet is
 * reactive. Missing parents are created as objects. Returns false for an
 * invalid path.
 */
export function setPath (obj, path, value) {
  var parts = splitPath(path)
  if (!parts || !isObjectLike(obj)) {
    return false
  }
  var cur = obj
  for (var i = 0; i < parts.length - 1; i++) {
    if (!isObjectLike(cur[parts[i]])) {
      Vue.set(cur, parts[i], {})
    }
    cur = cur[parts[i]]
  }
  Vue.set(cur, parts[parts.length - 1], value)
  return true
}

function pathStartsWith (path, prefix) {
  return path === prefix || path.indexOf(prefix + '.') === 0
}

/** Equal to, above, or beneath: the relation the API's conflict check uses. */
function pathsRelated (a, b) {
  return pathStartsWith(a, b) || pathStartsWith(b, a)
}

/* ------------------------------------------------------------------------
   Values: clone, normalisers, lenient equality (plan F2)
------------------------------------------------------------------------ */

/** Deep clone of a JSON-shaped value. */
export function cloneValue (v) {
  if (v === undefined) {
    return undefined
  }
  try {
    return JSON.parse(JSON.stringify(v))
  } catch (e) {
    return v
  }
}

function isPlainObject (v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

// Leaves that mean "nothing here": the comparison treats them as one value.
function isEmptyLeaf (v) {
  return v === undefined || v === null || v === false || v === ''
}

function isEmptyDeep (v) {
  if (isEmptyLeaf(v)) {
    return true
  }
  if (Array.isArray(v)) {
    return v.length === 0
  }
  if (isPlainObject(v)) {
    var keys = Object.keys(v)
    for (var i = 0; i < keys.length; i++) {
      if (!isEmptyDeep(v[keys[i]])) {
        return false
      }
    }
    return true
  }
  return false
}

function boolLike (v) {
  return v === true || v === 1 || v === '1' || v === 'true'
}

function leafEqual (a, b) {
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return boolLike(a) === boolLike(b)
  }
  // el-input type=number yields strings; the API stores numbers. Compare
  // numerically only when one side really IS a number, so two strings such as
  // a site id '007' and '7' are still different.
  if ((typeof a === 'number' || typeof b === 'number') && a !== '' && b !== '' && !isNaN(Number(a)) && !isNaN(Number(b))) {
    return Number(a) === Number(b)
  }
  return String(a) === String(b)
}

function lenientEqual (a, b) {
  if (isEmptyDeep(a) && isEmptyDeep(b)) {
    return true
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false
    }
    for (var i = 0; i < a.length; i++) {
      if (!lenientEqual(a[i], b[i])) {
        return false
      }
    }
    return true
  }
  if (isPlainObject(a) || isPlainObject(b)) {
    if (!isPlainObject(a) || !isPlainObject(b)) {
      return false
    }
    var keys = Object.keys(a)
    var bk = Object.keys(b)
    for (var j = 0; j < bk.length; j++) {
      if (keys.indexOf(bk[j]) === -1) {
        keys.push(bk[j])
      }
    }
    for (var k = 0; k < keys.length; k++) {
      if (!lenientEqual(a[keys[k]], b[keys[k]])) {
        return false
      }
    }
    return true
  }
  return leafEqual(a, b)
}

/**
 * HTML remarks. Equal when the raw strings match; or both are empty
 * ('', null, quill's '<p><br></p>'); or one is tag-free text and the other is
 * exactly '<p>' + text + '</p>' (vue-quill-editor re-pastes and emits that when
 * its value prop changes later). A bold-only edit is NOT equal.
 */
var EMPTY_HTML = /^(<p>(<br\s*\/?>)?<\/p>)+$/i

function isEmptyHtml (v) {
  if (v === undefined || v === null) {
    return true
  }
  if (typeof v !== 'string') {
    return false
  }
  var s = v.trim()
  return s === '' || EMPTY_HTML.test(s)
}

function htmlEqual (a, b) {
  if (a === b) {
    return true
  }
  var ea = isEmptyHtml(a)
  var eb = isEmptyHtml(b)
  if (ea || eb) {
    return ea && eb
  }
  if (typeof a !== 'string' || typeof b !== 'string') {
    return false
  }
  if (a.indexOf('<') === -1 && b === '<p>' + a + '</p>') {
    return true
  }
  if (b.indexOf('<') === -1 && a === '<p>' + b + '</p>') {
    return true
  }
  return false
}

function lastSegment (path) {
  var parts = splitPath(path)
  return parts ? parts[parts.length - 1] : ''
}

function isHtmlPath (path) {
  var last = lastSegment(path)
  return last === 'remarks' || last === 'resubmitRemarks'
}

function isDatePath (path) {
  var last = lastSegment(path)
  return last === 'targetDate' || last === 'actualDate' || /_at$/.test(last)
}

/**
 * Dates compare through the app clock, never through new parsing (XOL-414):
 * `toAppDateTimeSql` handles ISO 'Z', SQL datetimes and date-only strings.
 * Empty values are null.
 */
function datesEqual (a, b) {
  var ea = isEmptyLeaf(a)
  var eb = isEmptyLeaf(b)
  if (ea || eb) {
    return ea && eb
  }
  var na = toAppDateTimeSql(a)
  var nb = toAppDateTimeSql(b)
  if (na === null || nb === null) {
    return String(a) === String(b)
  }
  return na === nb
}

function deleteRelative (obj, segs) {
  var cur = obj
  for (var i = 0; i < segs.length - 1; i++) {
    if (!isObjectLike(cur)) {
      return
    }
    cur = cur[segs[i]]
  }
  if (isObjectLike(cur)) {
    delete cur[segs[segs.length - 1]]
  }
}

// When a compared path sits ABOVE a server-owned path (the `state` column
// itself, say), the owned sub-path must not make two values differ.
function stripOwned (path, value) {
  var parts = splitPath(path)
  if (!parts || !isObjectLike(value)) {
    return value
  }
  var out = value
  for (var i = 0; i < SERVER_OWNED_PATHS.length; i++) {
    var owned = splitPath(SERVER_OWNED_PATHS[i])
    if (owned.length > parts.length && pathStartsWith(SERVER_OWNED_PATHS[i], path)) {
      if (out === value) {
        out = cloneValue(value)
      }
      deleteRelative(out, owned.slice(parts.length))
    }
  }
  return out
}

/**
 * The pure transform behind RequestMixins.fixExistingEquipment: returns a NEW
 * list where an item whose `type` is an array is split into category / type /
 * subType / variant, and a missing `name` is built from them.
 */
export function normaliseEquipment (list) {
  if (!Array.isArray(list)) {
    return list
  }
  var out = []
  for (var n = 0; n < list.length; n++) {
    var src = list[n]
    if (!isPlainObject(src)) {
      out.push(src)
      continue
    }
    var item = cloneValue(src)
    if (Array.isArray(item.type)) {
      var types = item.type
      item.category = null
      item.subType = null
      item.variant = null
      for (var i = 0; i < types.length; i++) {
        if (types[i] && i === 0) {
          item.category = types[i]
        }
        if (types[i] && i === 2) {
          item.subType = types[i]
        }
        if (types[i] && i === 3) {
          item.variant = types[i]
        }
      }
      item.type = types[1]
    }
    if (!item.name) {
      item.name = (item.category ? item.category : '')
      item.name += (item.category ? ' / ' + item.type : item.type)
      item.name += (item.subType ? ' / ' + item.subType : '')
      item.name += (item.variant ? ' / ' + item.variant : '')
    }
    out.push(item)
  }
  return out
}

// The decision paths. A status of null, '' and 'Pending' is one value (the API's
// StepApprovalRules::status).
var DECISION_STATUS_PATH = 'approvedStatus'
var DECISION_DATE_PATH = 'approved_at'

function decisionStatus (v) {
  var s = v === undefined || v === null ? '' : String(v)
  return s === '' ? 'Pending' : s
}

/**
 * The calendar day of a decision date, as the wire sends it (Y-m-d) and as the
 * comparison reads it: through the app clock, the same transform as
 * DateMixins.formatDateSql, never a JS Date parse (XOL-414). '' when there is none.
 */
export function decisionDay (v) {
  if (v === undefined || v === null || v === '') {
    return ''
  }
  var m = toAppTime(v)
  return m === null ? String(v) : m.format(DATE_SQL_FORMAT)
}

/**
 * Is `a` the same saved value as `b` for this path? "Same" is lenient on
 * purpose, so a programmatic rewrite (quill's <p> wrapping, number vs string,
 * false vs null, an equipment row's derived name) never reads as an edit.
 */
export function valuesEqual (path, a, b) {
  // Decision paths (XOL-596). Never `datesEqual` for the date: `approved_at` is a
  // DATETIME that carries a calendar day (conventions section 4, XOL-414).
  if (path === DECISION_STATUS_PATH) {
    return decisionStatus(a) === decisionStatus(b)
  }
  if (path === DECISION_DATE_PATH) {
    return decisionDay(a) === decisionDay(b)
  }
  if (isHtmlPath(path)) {
    return htmlEqual(a, b)
  }
  if (isDatePath(path)) {
    return datesEqual(a, b)
  }
  if (path === 'state.step2.equipment') {
    return lenientEqual(normaliseEquipment(a), normaliseEquipment(b))
  }
  return lenientEqual(stripOwned(path, a), stripOwned(path, b))
}

/* ------------------------------------------------------------------------
   Path ownership: guard G-OWN (R2.10)
------------------------------------------------------------------------ */

function deny (reason) {
  return { ok: false, reason: reason }
}

var ALLOW = { ok: true, reason: null }

/**
 * Static check, whoever the viewer is: a valid v3 path, in a content column of
 * this row kind, never server-owned and never above a server-owned path.
 * `status` is content on a non-workflow action only.
 *
 * @param {String} kind        'step' | 'action'
 * @param {String} path
 * @param {String} actionType  the request's actionType (decides `status`)
 */
export function checkWritablePath (kind, path, actionType) {
  var parts = splitPath(path)
  if (!parts) {
    return deny('invalid-path')
  }
  var top = parts[0]
  if (kind === 'step' && STEP_DERIVED_KEYS.indexOf(top) !== -1) {
    return deny('derived')
  }
  var content = kind === 'step' ? STEP_CONTENT_KEYS.slice() : ACTION_CONTENT_KEYS.slice()
  if (kind === 'action' && WORKFLOW_ACTION_TYPES.indexOf(actionType) === -1) {
    content = content.concat(ACTION_NONWORKFLOW_CONTENT_KEYS)
  }
  if (content.indexOf(top) === -1) {
    return deny('not-a-content-column')
  }
  // Non-JSON columns take no sub-path.
  if (top !== 'state' && top !== 'emails' && parts.length > 1) {
    return deny('no-sub-path')
  }
  for (var l = 0; l < LIST_PATHS.length; l++) {
    if (path.indexOf(LIST_PATHS[l] + '.') === 0) {
      return deny('beneath-list')
    }
  }
  for (var i = 0; i < SERVER_OWNED_PATHS.length; i++) {
    // Equal to, beneath, or above a server-owned path.
    if (pathsRelated(path, SERVER_OWNED_PATHS[i])) {
      return deny('server-owned')
    }
  }
  if (top === 'emails' && EMAILS_WRITABLE_PATHS.indexOf(path) === -1) {
    return deny('emails-path-not-writable')
  }
  return ALLOW
}

/**
 * Who may send which path. One table, so no component decides on its own.
 *
 * ctx: { isTowerCo, actionType, isRequestOwner, isRequestProvider,
 *        partyNo (step rows), telcoEquipmentAllowed }
 *
 *  - Nobody: server-owned paths, and the columns the static check refuses.
 *    Parents of a protected path are refused too (XOL-595 A4): write LEAVES
 *    (`state.step3.docType`, never `state.step3` or `state`); arrays stay whole
 *    at their own path. `changedLeafPaths` / `fieldsChangedUnder` turn a changed
 *    object into leaf paths.
 *  - Tower Co: everything content, except on an RTB the counterparty-owned
 *    paths: `state.step5.resubmitRemarks`, and party-2 step `remarks`,
 *    `state.tpType`, `state.siteId` (contract v3 would answer 422). Actions
 *    `remarks` is shared on every type and the Tower Co may write it.
 *  - Counterparty on an RTB: actions `remarks`, `state.step5.resubmitRemarks`;
 *    party-2 step `remarks`, `state.tpType`, `state.siteId` (owner only). Never any
 *    `emails.*` path: Emails Update is Tower Co only (XOL-586 F1), so every `emails`
 *    path falls through to not-in-counterparty-allow-list.
 *  - The request owner on an RTS only, while the Tower Co allows client
 *    equipment input (the caller computes `telcoEquipmentAllowed`): actions
 *    `state.step2.equipment`. Never site equipment: this module has no way to
 *    address `/assets`.
 */
export function checkPathOwnership (ctx, kind, path) {
  ctx = ctx || {}
  var base = checkWritablePath(kind, path, ctx.actionType)
  if (!base.ok) {
    return base
  }
  var isRTB = ctx.actionType === 'RTB'
  var party2 = Number(ctx.partyNo) === 2

  if (kind === 'action') {
    if (ctx.isTowerCo) {
      // The path itself, and its PARENTS (`state.step5`): the API answers a write
      // to a parent of a protected path with 422 (XOL-595 A4). Write leaves.
      if (isRTB && pathsRelated(path, 'state.step5.resubmitRemarks')) {
        return deny('counterparty-owned')
      }
      return ALLOW
    }
    if (path === 'state.step2.equipment') {
      if (ctx.isRequestOwner && ctx.actionType === 'RTS' && ctx.telcoEquipmentAllowed) {
        return ALLOW
      }
      return deny('equipment-not-allowed')
    }
    if (!isRTB) {
      return deny('counterparty-outside-rtb')
    }
    if (path === 'remarks' || path === 'state.step5.resubmitRemarks') {
      return ALLOW
    }
    return deny('not-in-counterparty-allow-list')
  }

  // step rows
  if (ctx.isTowerCo) {
    if (isRTB && party2 && (path === 'remarks' || path === 'state.tpType' || path === 'state.siteId')) {
      return deny('counterparty-owned')
    }
    return ALLOW
  }
  if (!isRTB || !party2) {
    return deny('counterparty-own-row-only')
  }
  if (path === 'remarks' || path === 'state.tpType') {
    return ALLOW
  }
  if (path === 'state.siteId') {
    return ctx.isRequestOwner ? ALLOW : deny('owner-only')
  }
  return deny('not-in-counterparty-allow-list')
}

/* ------------------------------------------------------------------------
   Rows: records, shadows, entries (R2.5)
------------------------------------------------------------------------ */

function inferKind (row) {
  if (row && (row.stepNo !== undefined || row.action_id !== undefined)) {
    return 'step'
  }
  return 'action'
}

function contentKeysFor (kind) {
  return kind === 'step'
    ? STEP_CONTENT_KEYS
    : ACTION_CONTENT_KEYS.concat(ACTION_NONWORKFLOW_CONTENT_KEYS)
}

function toVersion (v) {
  if (v === undefined || v === null || v === '') {
    return null
  }
  var n = Number(v)
  return isNaN(n) ? null : n
}

// A deep copy of the content columns, the "last server value" of every path
// that has no entry. Taken at registration and replaced on every adopt.
function snapshotContent (row, kind) {
  var out = {}
  var keys = contentKeysFor(kind)
  for (var i = 0; i < keys.length; i++) {
    if (row && row[keys[i]] !== undefined) {
      out[keys[i]] = cloneValue(row[keys[i]])
    }
  }
  return out
}

// The last SERVER value of every workflow-owned column of a row (the keys only
// a command writes), and for an action also `state.step3.legalApproved`. A
// command's `expected...` value comes from here, never from the v-model, which
// has already changed by the time its @change fires. Taken at registration and
// refreshed by every adopt that carries the key.
var LEGAL_PATH = 'state.step3.legalApproved'

function ownedKeysFor (kind) {
  return kind === 'step' ? STEP_OWNED_KEYS : ACTION_OWNED_KEYS
}

function snapshotOwned (row, kind) {
  var out = {}
  var keys = ownedKeysFor(kind)
  for (var i = 0; i < keys.length; i++) {
    if (row && row[keys[i]] !== undefined) {
      out[keys[i]] = cloneValue(row[keys[i]])
    }
  }
  if (kind === 'action' && hasPath(row, LEGAL_PATH)) {
    out[LEGAL_PATH] = cloneValue(getPath(row, LEGAL_PATH))
  }
  return out
}

function newRecord (row, kind) {
  return {
    kind: kind,
    row: row,
    id: row ? row.id : undefined,
    queueKey: kind + ':' + (row ? row.id : ''),
    scope: null,
    sender: null,
    // XOL-596 (decision fields): sends the approval command of a decision group (see
    // registerDecision), and the group itself. null on a row with no decision.
    decisionSender: null,
    decision: null,
    // Which form owns the row, for the notices' row label: null (a request
    // drawer), 'actionDetails' (Site -> Actions edit) or 'progressStep' (PRG).
    form: null,
    // An EditPane form is open on this row (holdPaths .. releasePaths / revertRow):
    // its unsaved edits are the user's DRAFT, saved only by the form's own Save.
    // They are not "pending" for a drawer close, a navigation or a logout, and no
    // flush point saves them (Carlos: no auto-save on these forms).
    draft: false,
    released: false,
    lastAdoptedVersion: toVersion(row && row.contentVersion),
    lastAdoptedSeq: 0,
    shadow: snapshotContent(row, kind),
    // Workflow-owned columns as the server last gave them (see snapshotOwned).
    workflow: snapshotOwned(row, kind),
    // Owned keys / paths a running COMMAND is deciding: { key: count }. An adopt
    // leaves their local value alone (see holdOwned).
    heldOwned: {},
    // THE WORKFLOW FLOOR. Commands do not bump contentVersion, so for the
    // workflow-owned keys the seq guard alone cannot tell a snapshot that read the
    // row before a command committed from one that read it after. Per row:
    //   commandsInFlight  commands sent on this row and not answered yet: every
    //                     other adopt skips the row's owned keys (display AND
    //                     baseline) meanwhile;
    //   ownedFloor        the seq taken when the last command was answered: an
    //                     adopt issued before it (seq < floor) skips the owned
    //                     keys; one issued after it applies them.
    // A command's own response (and its 409 body) sets the owned keys whatever
    // its seq. Content keys keep their own rules.
    commandsInFlight: 0,
    ownedFloor: 0,
    // The contentVersion of the response each content COLUMN was last taken
    // from. A response that omits a column does not make us "up to date" on
    // it, so a new field's base comes from here, not from lastAdoptedVersion.
    columnVersion: initialColumnVersions(row, kind),
    // When the row last took a server response (Date.now()); the window-focus
    // refetch (R2.15) reads it.
    adoptedAt: Date.now(),
    entries: {},
    deferred: {},
    // Per list path: what the user did to the list since its base (R2.9), so a
    // 409 on a recipient list can re-apply it onto the server's current list.
    ops: {}
  }
}

function initialColumnVersions (row, kind) {
  var out = {}
  var v = toVersion(row && row.contentVersion)
  var snap = snapshotContent(row, kind)
  Object.keys(snap).forEach(function (k) {
    out[k] = v
  })
  return out
}

// The base version for a NEW entry on `path`: the version of the response its
// column's value came from (G-BASE).
function columnBase (rec, path) {
  var v = rec.columnVersion[path.split('.')[0]]
  return v === undefined ? rec.lastAdoptedVersion : v
}

function isIdleRecord (rec) {
  return Object.keys(rec.entries).length === 0
}

/**
 * Register a row (call once per row, as soon as it is loaded and before any
 * auto-fill that should count as an edit).
 *
 * Idempotent: registering an already-registered row only (re)attaches its scope
 * and sender; it never resets the baseline, because the row may carry unsaved
 * edits by then. A released, idle record is re-initialised.
 *
 * @param {Object} row   the ONE object for this row (actionData / a step row)
 * @param {String} kind  'step' | 'action'
 * @param {Object} opts  { scope, sender, decisionSender, form } sender(req) -> Promise<outcome>;
 *                       decisionSender(req) sends a decision group's approval command;
 *                       form: 'actionDetails' | 'progressStep' names the row in notices
 * @return {Object} the record
 */
export function registerRow (row, kind, opts) {
  opts = opts || {}
  if (!isObjectLike(row)) {
    return null
  }
  var rec = recordsByRow.get(row)
  if (rec && rec.released && isIdleRecord(rec)) {
    rec = null
  }
  if (!rec) {
    rec = newRecord(row, kind || inferKind(row))
    recordsByRow.set(row, rec)
    allRecords.push(rec)
  }
  rec.released = false
  if (opts.sender) {
    rec.sender = opts.sender
  }
  if (opts.decisionSender) {
    rec.decisionSender = opts.decisionSender
  }
  if (opts.form) {
    rec.form = opts.form
  }
  if (opts.scope) {
    rec.scope = opts.scope
    if (opts.scope.rows.indexOf(rec) === -1) {
      opts.scope.rows.push(rec)
    }
  }
  return rec
}

/** The live record of row `id` of `kind` ('step' | 'action'), or undefined. */
export function findRecord (kind, id) {
  for (var i = 0; i < allRecords.length; i++) {
    if (allRecords[i].kind === kind && Number(allRecords[i].id) === Number(id)) {
      return allRecords[i]
    }
  }
  return undefined
}

/** The record of a row, or undefined when it was never registered. */
export function getRecord (row) {
  return isObjectLike(row) ? recordsByRow.get(row) : undefined
}

/* ------------------------------------------------------------------------
   Decision fields (XOL-596, Carlos 2026-10-14)

   The approval controls are fields: a change marks the field changed and restarts
   the form's pause, and at the pause the group's latest values go out ONCE, as one
   approval command. A GROUP is the set of paths one command decides:

     step2   the party-1 step row     approvedStatus, approved_at
     step5   the party-1 step row     approvedStatus, approved_at
     step3   the ACTION row           state.step3.legalApproved

   The group is held as a unit, and the command's `expected...` value is the
   group's BASE (G-DECIDE): the decision the user saw when they first changed it,
   never the baseline (an adopt during the pause may have moved that to someone
   else's decision, which the user never saw).
------------------------------------------------------------------------ */

/**
 * Register the decision group that lives on `row` (the record that holds the
 * v-model).
 *
 * @param {Object} row
 * @param {Object} spec  { kind: 'step2' | 'step3' | 'step5', paths: [..],
 *                         commandRow: the step row the POST addresses,
 *                         rows: [rows the command queues behind] }
 * @return {Object} the record
 */
export function registerDecision (row, spec) {
  var rec = recordFor(row)
  if (!rec || !spec || !Array.isArray(spec.paths)) {
    return null
  }
  rec.decision = {
    kind: spec.kind,
    paths: spec.paths.slice(),
    commandRow: spec.commandRow,
    rows: (spec.rows || [row]).slice()
  }
  return rec
}

function decisionSpecFor (rec, path) {
  var d = rec.decision
  return d && d.paths.indexOf(path) !== -1 ? d : null
}

// The value a decision path had when the user last SAW the server's word: the
// workflow baseline (`rec.workflow`), never the content shadow (which has no owned
// keys). `changed` is the path the user is changing right now: its screen value
// is already the new one, so a missing baseline is "none" (null), while for any
// other path of the group the screen still shows the base.
export function decisionBase (rec, path, changed) {
  var v = rec.workflow[path]

  if (v === undefined && path === LEGAL_PATH && rec.decision && rec.decision.commandRow) {
    // Step 3 falls back to the step's `approved`, as the tick always has.
    var cmd = getRecord(rec.decision.commandRow)
    var flag = cmd ? cmd.workflow.approved : rec.decision.commandRow.approved
    return !!flag
  }
  if (v === undefined) {
    return path === changed ? null : cloneValue(getPath(rec.row, path))
  }
  return cloneValue(v)
}

// Set the workflow-owned keys a command's own response carries, whatever its
// seq, and move the baseline with them (the workflow floor, rule a).
function applyOwnedFromCommand (rec, target, serverRow) {
  var owned = ownedKeysFor(rec.kind)
  for (var i = 0; i < owned.length; i++) {
    if (serverRow[owned[i]] !== undefined) {
      Vue.set(target, owned[i], cloneValue(serverRow[owned[i]]))
      rec.workflow[owned[i]] = cloneValue(serverRow[owned[i]])
    }
  }
  if (rec.kind === 'action' && hasPath(serverRow, LEGAL_PATH)) {
    assignPath(target, LEGAL_PATH, cloneValue(getPath(serverRow, LEGAL_PATH)))
    rec.workflow[LEGAL_PATH] = cloneValue(getPath(serverRow, LEGAL_PATH))
  }
}

/**
 * A workflow command is now in flight on these rows (call when it is SENT; pair
 * every call with commandAnswered). Meanwhile every other adopt skips the rows'
 * owned keys, for the display and the baseline: a snapshot issued after the
 * command may still have read the row before it committed.
 */
export function commandSent (rows) {
  for (var i = 0; i < rows.length; i++) {
    var rec = recordFor(rows[i])
    if (rec) {
      rec.commandsInFlight++
    }
  }
}

/**
 * The command was answered (a response, a refusal, a network failure): it is no
 * longer in flight, and the floor moves to NOW, so a snapshot issued before this
 * moment can never set the owned keys again. A snapshot issued after it can.
 */
export function commandAnswered (rows) {
  for (var i = 0; i < rows.length; i++) {
    var rec = getRecord(rows[i])
    if (rec) {
      rec.commandsInFlight = Math.max(0, rec.commandsInFlight - 1)
      rec.ownedFloor = nextSeq()
      // A released record kept alive only by this command is dropped now.
      if (rec.released && rec.commandsInFlight === 0) {
        prune()
      }
    }
  }
}

/**
 * The baseline of a row's workflow-owned columns: what the SERVER last said, not
 * what the screen shows. `getBaseline(step).approvedStatus` is the
 * `expectedApprovedStatus` of an approval command; `Boolean(getBaseline(step)
 * .approved)` its `expectedApproved`. For an action, `['state.step3.legalApproved']`
 * is the legal tick as the server holds it. A copy; a row that was never
 * registered answers from what it holds now.
 */
export function getBaseline (row) {
  var rec = getRecord(row)
  if (rec) {
    return cloneValue(rec.workflow)
  }
  return isObjectLike(row) ? snapshotOwned(row, inferKind(row)) : {}
}

/**
 * One workflow-owned column of a row as the SERVER last said it, without the copy
 * `getBaseline` makes (templates call this on every render; `completed` is the
 * first user). A row that was never registered answers from what it holds now.
 */
export function baselineValue (row, key) {
  var rec = getRecord(row)
  if (rec) {
    return rec.workflow[key]
  }
  return isObjectLike(row) ? snapshotOwned(row, inferKind(row))[key] : undefined
}

/**
 * Hold workflow-owned keys (or dotted paths) of a row for the length of a COMMAND
 * on them: the control shows the user's choice and its in-flight indicator, and
 * an adopt of that row meanwhile (a field-save response, a refetch) does not flip
 * it back to the server's old value. The BASELINE still takes the adopt, so a
 * failed command reverts to the newest server truth. Counted per key; release
 * with the same keys, exactly once per hold.
 */
export function holdOwned (row, keys) {
  var rec = recordFor(row)
  if (!rec) {
    return false
  }
  for (var i = 0; i < keys.length; i++) {
    rec.heldOwned[keys[i]] = (rec.heldOwned[keys[i]] || 0) + 1
  }
  return true
}

export function releaseOwned (row, keys) {
  var rec = getRecord(row)
  if (!rec) {
    return false
  }
  for (var i = 0; i < keys.length; i++) {
    if (rec.heldOwned[keys[i]]) {
      rec.heldOwned[keys[i]]--
      if (rec.heldOwned[keys[i]] <= 0) {
        delete rec.heldOwned[keys[i]]
      }
    }
  }
  return true
}

/**
 * The record of a row, registering it on the spot when it is not registered
 * yet. A late registration baselines from whatever the row holds now, so
 * components should register on load.
 */
export function recordFor (row, kind) {
  var rec = getRecord(row)
  if (!rec) {
    rec = registerRow(row, kind)
  }
  return rec
}

/**
 * Mark a row's record released (its drawer closed). It is dropped as soon as it
 * has no field left to save; a field whose save failed on the network keeps it
 * alive, so "pending" survives the drawer.
 */
export function releaseRow (row) {
  var rec = getRecord(row)
  if (rec) {
    rec.released = true
    prune()
  }
}

// A released record with no entry left is dropped, unless a queue still runs for it or a
// workflow command is in flight on its row (the leave prompt counts that command; the
// record is pruned when `commandAnswered` settles it).
function prune () {
  allRecords = allRecords.filter(function (rec) {
    return !(rec.released && isIdleRecord(rec) && !queues[rec.queueKey] && rec.commandsInFlight === 0)
  })
  scopes = scopes.filter(function (scope) {
    scope.rows = scope.rows.filter(function (rec) {
      return allRecords.indexOf(rec) !== -1
    })
    return !(scope.destroyed && scope.rows.length === 0)
  })
}

// A record that was released (its drawer closed) and is pruned from the
// registry comes back to life when its field is touched again, so a change made
// after the close is still pending and a flush point still saves it.
function reviveRecord (rec) {
  rec.released = false
  if (allRecords.indexOf(rec) === -1) {
    allRecords.push(rec)
  }
}

function newEntry (rec, path, value) {
  return {
    path: path,
    // The base value: what the server last gave us for this field. The
    // pause-flush and the deferred apply compare against it.
    value: cloneValue(value),
    // G-BASE: an INTEGER row contentVersion, the version of the response this
    // field's displayed value came from. Never a fieldVersions entry.
    baseVersion: columnBase(rec, path),
    focused: false,
    inFlight: false,
    inFlightSeq: null,
    // The value that was sent, and the promise of its outcome, while in flight.
    inFlightValue: undefined,
    inFlightPromise: null,
    queued: false,
    queuedPromise: null,
    // fieldChanged ran for this entry and its save has not settled: a change the sync
    // layer KNOWS about. A focused editor's v-model writes the keystroke before any handler
    // runs, so a dirty value alone does not say that (restoreServerValue reads this).
    recorded: false,
    networkFailed: false,
    // The status of this field's last save outcome (see redirtied).
    lastOutcome: null,
    cue: null
  }
}

// Create an entry. `fromShadow` is for a change: by the time `@change` fires the
// model already holds the NEW value, so the base value is the last server value
// (the shadow). Focus has no such problem: the field still holds its base value.
function ensureEntry (rec, path, fromShadow) {
  var entry = rec.entries[path]
  var spec = decisionSpecFor(rec, path)
  if (spec) {
    // A decision path is held WITH its group: the first change of any path creates
    // an entry for every path of the group (a clean one carries its base), so a
    // date-only change cannot leave the status open to someone else's decision.
    // The base is the workflow baseline (G-DECIDE), not the content shadow.
    spec.paths.forEach(function (p) {
      if (!rec.entries[p]) {
        rec.entries[p] = newEntry(rec, p, decisionBase(rec, p, fromShadow ? path : null))
      }
    })
    return rec.entries[path]
  }
  if (!entry) {
    var base = fromShadow ? getPath(rec.shadow, path) : getPath(rec.row, path)
    entry = newEntry(rec, path, base)
    rec.entries[path] = entry
  }
  return entry
}

function isDirty (rec, path) {
  var entry = rec.entries[path]
  return !!entry && !valuesEqual(path, getPath(rec.row, path), entry.value)
}

function isHeldEntry (rec, path) {
  var entry = rec.entries[path]
  return !!entry && (entry.focused || entry.inFlight || entry.queued || isDirty(rec, path))
}

// Held = focused, dirty, queued or in flight. A decision path is held while ANY path
// of its group is (D1), so the group stays on screen as the user left it.
function isHeld (rec, path) {
  var spec = decisionSpecFor(rec, path)
  if (spec) {
    return !!rec.entries[path] && spec.paths.some(function (p) {
      return isHeldEntry(rec, p)
    })
  }
  return isHeldEntry(rec, path)
}

// Stop holding a field: apply the parked server value (when the field is clean
// and it differs from what is shown), refresh the shadow, drop the entry.
function dropEntry (rec, path) {
  var entry = rec.entries[path]
  if (!entry) {
    return
  }
  var final = entry.value
  var deferred = rec.deferred[path]
  if (deferred) {
    if (!valuesEqual(path, getPath(rec.row, path), deferred.value)) {
      assignPath(rec.row, path, cloneValue(deferred.value))
    }
    final = deferred.value
  }
  // A decision path is server-owned: it never enters the content shadow.
  if (!decisionSpecFor(rec, path)) {
    setPath(rec.shadow, path, cloneValue(final))
  }
  delete rec.entries[path]
  delete rec.deferred[path]
  delete rec.ops[path]
  bump()
}

function releaseIfIdle (rec, path) {
  if (rec.entries[path] && !isHeld(rec, path)) {
    dropEntry(rec, path)
  }
}

// Drop an entry without touching the displayed value or the shadow (a path
// that can never be sent: a G-OWN refusal).
function abandonEntry (rec, path) {
  delete rec.entries[path]
  delete rec.deferred[path]
  delete rec.ops[path]
  bump()
}

/**
 * The path takes `value` (the server's), whatever the user typed: used after a
 * 409 stale_field, a 403 lock or a 422. The entry is dropped.
 */
export function takeServer (rec, path, value) {
  var decision = !!decisionSpecFor(rec, path)
  if (value !== undefined) {
    assignPath(rec.row, path, cloneValue(value))
    if (!decision) {
      setPath(rec.shadow, path, cloneValue(value))
    }
  } else if (rec.deferred[path]) {
    assignPath(rec.row, path, cloneValue(rec.deferred[path].value))
    if (!decision) {
      setPath(rec.shadow, path, cloneValue(rec.deferred[path].value))
    }
  } else if (decision) {
    // The last word the server gave about a decision lives in the workflow
    // baseline, not in the content shadow (which has no owned keys).
    var known = decisionBase(rec, path, path)
    if (known !== undefined && (rec.workflow[path] !== undefined || path === LEGAL_PATH)) {
      assignPath(rec.row, path, known)
    }
  } else if (hasPath(rec.shadow, path)) {
    // No server value at hand (the recovery GET failed): fall back to the last
    // server value we know, so a refused value does not sit on screen looking
    // saved.
    assignPath(rec.row, path, cloneValue(getPath(rec.shadow, path)))
  }
  delete rec.entries[path]
  delete rec.deferred[path]
  delete rec.ops[path]
  bump()
  refreshScope(rec.scope)
}

/* ------------------------------------------------------------------------
   Field hooks used by templates (R2.4)
------------------------------------------------------------------------ */

// A row must be registered on load, before its first edit: a late registration
// baselines from whatever the row holds NOW, so that first change reads as clean
// and is never saved.
function warnIfUnregistered (row, fn, path) {
  if (isObjectLike(row) && !recordsByRow.get(row)) {
    console.error('XOL-596: ' + fn + '(' + path + ') on a row that was never registered; its first change will not be saved. Call registerRow on load.')
  }
}

/** `@focus` of a text-type input. */
export function holdField (row, path) {
  warnIfUnregistered(row, 'holdField', path)
  var rec = recordFor(row)
  if (!rec || !isValidPath(path)) {
    return false
  }
  reviveRecord(rec)
  ensureEntry(rec, path, false).focused = true
  refreshScope(rec.scope)
  bump()
  return true
}

/**
 * `@blur`. Blur never saves (rev 3). A clean field is released (its parked
 * server value is applied); a dirty one stays held until its pause-flush.
 */
export function releaseField (row, path) {
  var rec = getRecord(row)
  var entry = rec && rec.entries[path]
  if (!entry) {
    return false
  }
  entry.focused = false
  releaseIfIdle(rec, path)
  refreshScope(rec.scope)
  bump()
  return true
}

/**
 * Every content-field `input` / `change` handler and every list button calls
 * this AFTER the model changed. It marks the field changed and restarts the
 * form's 3 s timer; nothing is sent here.
 */
export function fieldChanged (row, path) {
  warnIfUnregistered(row, 'fieldChanged', path)
  var rec = recordFor(row)
  if (!rec) {
    return false
  }
  if (!isValidPath(path)) {
    console.error('XOL-596: fieldChanged with an invalid path', path)
    return false
  }
  reviveRecord(rec)
  ensureEntry(rec, path, true).recorded = true
  if (rec.scope) {
    restartPause(rec.scope)
  }
  refreshScope(rec.scope)
  bump()
  return true
}

/** The "changed underneath you" cue of a field, or null. Reactive. */
export function fieldCue (row, path) {
  track()
  var rec = getRecord(row)
  var entry = rec && rec.entries[path]
  return entry ? entry.cue : null
}

/** Does the field differ from its base value? Reactive (reads the model). */
export function fieldDirty (row, path) {
  track()
  var rec = getRecord(row)
  return !!rec && isDirty(rec, path)
}

/** Is the field's input focused right now? Reactive. */
export function fieldFocused (row, path) {
  track()
  var rec = getRecord(row)
  var entry = rec && rec.entries[path]
  return !!entry && !!entry.focused
}

/** Is the field held (focused, dirty, queued or in flight)? Reactive. */
export function fieldHeld (row, path) {
  track()
  var rec = getRecord(row)
  return !!rec && isHeld(rec, path)
}

/**
 * "Use their version" (UF11): put the parked server value on the field while it
 * is still held, rebase its entry to the version that value came from, clear
 * the cue. The next save carries the NEW base.
 */
export function takeServerValue (row, path) {
  var rec = getRecord(row)
  var entry = rec && rec.entries[path]
  var deferred = rec && rec.deferred[path]
  if (!entry || !deferred) {
    if (entry) {
      entry.cue = null
      bump()
    }
    return false
  }
  assignPath(rec.row, path, cloneValue(deferred.value))
  entry.value = cloneValue(deferred.value)
  entry.baseVersion = deferred.version
  entry.recorded = false
  entry.cue = null
  delete rec.deferred[path]
  refreshScope(rec.scope)
  bump()
  return true
}

/** EditPane Edit: hold every editable path (R2.16). */
export function holdPaths (row, paths) {
  var rec = recordFor(row)
  if (!rec) {
    return false
  }
  reviveRecord(rec)
  for (var i = 0; i < paths.length; i++) {
    if (isValidPath(paths[i])) {
      ensureEntry(rec, paths[i], false).focused = true
    }
  }
  rec.draft = true
  bump()
  return true
}

/**
 * EditPane Save: release them; clean ones apply their parked value. The form is no
 * longer a draft: whatever is still dirty is about to be saved by its Save.
 */
export function releasePaths (row, paths) {
  var rec = getRecord(row)
  if (!rec) {
    return false
  }
  rec.draft = false
  for (var i = 0; i < paths.length; i++) {
    var entry = rec.entries[paths[i]]
    if (entry) {
      entry.focused = false
      releaseIfIdle(rec, paths[i])
    }
  }
  bump()
  return true
}

/** EditPane Cancel: put the base value back on the field and release it. */
export function revertField (row, path) {
  var rec = getRecord(row)
  var entry = rec && rec.entries[path]
  if (!entry) {
    return false
  }
  assignPath(rec.row, path, cloneValue(entry.value))
  entry.focused = false
  releaseIfIdle(rec, path)
  bump()
  return true
}

/**
 * A control that is read-only (a locked or completed approval) reached its change
 * handler anyway (2026-10-15, XOL-596): put the server's last content value back on
 * the displayed field. The control's v-model has already written the new value, so the
 * field may read dirty (a focused editor holds an entry from its focus), but nothing
 * was RECORDED (`fieldChanged` never ran) and nothing is sent.
 *
 * A change that IS recorded (in flight, queued, or `entry.recorded` and still dirty) is
 * left alone: it is already on its way, the server answers it (403) and the stash keeps
 * the user's text. The value put back is the entry's base (what the server last gave
 * us), else the shadow's; a path the server never had goes back to `undefined`, so an
 * unrecorded pick does not stay on screen. Returns true when the value was put back.
 */
export function restoreServerValue (row, path) {
  var rec = getRecord(row)
  if (!rec) {
    return false
  }
  var entry = rec.entries[path]
  if (entry && (entry.inFlight || entry.queued || (entry.recorded && isDirty(rec, path)))) {
    return false
  }
  var known = entry ? entry.value : (hasPath(rec.shadow, path) ? getPath(rec.shadow, path) : undefined)
  assignPath(rec.row, path, cloneValue(known))
  if (entry) {
    entry.focused = false
    releaseIfIdle(rec, path)
  }
  bump()
  return true
}

/**
 * EditPane Cancel (ActionDetails, ProgressStep): put the columns `keys` (default:
 * every content column) back to what the server last said, IN PLACE (the form
 * aliases `state` and its lists), and release every field of the row. A newer
 * server value parked while the user was editing (an adopt skipped the held
 * field) is applied after the baseline, so Cancel shows the newest truth.
 * Unlike a bare `resetData()` it never blanks the row: the baseline is the row.
 *
 * @param {Object} row
 * @param {Array}  keys  content columns to restore (a column the shadow lacks is
 *                       left alone)
 */
export function revertRow (row, keys) {
  var rec = getRecord(row)
  if (!rec) {
    return false
  }
  var cols = keys || contentKeysFor(rec.kind)
  rec.draft = false
  cols.forEach(function (key) {
    if (hasPath(rec.shadow, key)) {
      assignPath(rec.row, key, cloneValue(getPath(rec.shadow, key)))
    }
  })
  Object.keys(rec.entries).forEach(function (path) {
    var entry = rec.entries[path]
    if (entry.inFlight || entry.queued) {
      return
    }
    entry.focused = false
    dropEntry(rec, path)
  })
  refreshScope(rec.scope)
  bump()
  return true
}

function collectAllLeaves (prefix, value, out) {
  if (isPlainObject(value) && Object.keys(value).length > 0) {
    Object.keys(value).forEach(function (key) {
      var child = prefix + '.' + key
      if (!isValidPath(child)) {
        console.error('XOL-596: skipping a key that is not a valid path segment', child)
        return
      }
      collectAllLeaves(child, value[key], out)
    })
    return
  }
  for (var i = 0; i < SERVER_OWNED_PATHS.length; i++) {
    if (pathStartsWith(prefix, SERVER_OWNED_PATHS[i])) {
      return
    }
  }
  out.push(prefix)
}

/**
 * Every LEAF path of the columns `keys` as the row holds them now (a plain object
 * is walked; a scalar, null, an empty object and an array are leaves, arrays
 * whole), skipping server-owned paths. What an EditPane form holds on Edit
 * (R2.16): `leafPaths(row, ['state'])` for a step whose `state` is the form.
 */
export function leafPaths (row, keys) {
  var out = []
  ;(keys || []).forEach(function (key) {
    if (isValidPath(key) && hasPath(row, key)) {
      collectAllLeaves(key, getPath(row, key), out)
    }
  })
  return out
}

/* ------------------------------------------------------------------------
   Lists (R2.9) and what is left to save
------------------------------------------------------------------------ */

/**
 * Record what the user did to a list path since its base: { type: 'add' |
 * 'remove', key, item }. A 409 on a recipient list re-applies these onto the
 * server's current list (by key) and resubmits once.
 */
export function recordListOp (row, path, op) {
  var rec = recordFor(row)
  if (!rec) {
    return false
  }
  rec.ops[path] = (rec.ops[path] || []).concat([cloneValue(op)])
  return true
}

export function getListOps (rec, path) {
  return (rec.ops[path] || []).slice()
}

// Has the field something left to save? A queued or in-flight save, or a held
// change. The unsaved edits of an open EditPane form (a draft) are not pending.
function isPendingEntry (rec, entry, path, withDrafts) {
  return !!(entry.inFlight || entry.queued || ((withDrafts || !rec.draft) && isDirty(rec, path)))
}

/**
 * Every field with something left to save, for the "changes have NOT been saved
 * yet" dialog and the logout dialog: [{ meta, path, yours, inFlight, failed }].
 * `yours` is the value on screen now. The unsaved edits of an open EditPane form
 * (a draft) are listed only with `{drafts: true}`: nothing saves them, but a
 * reload or a logout would lose them (the 428 prompt and the logout dialog).
 * `{dirtyOnly: true}` lists only fields whose screen value differs from the server's.
 */
export function pendingFields (opts) {
  var withDrafts = !!(opts && opts.drafts)
  var scope = opts && opts.scope
  var unguarded = !!(opts && opts.unguarded)
  // `dirtyOnly`: list a field only if what is on screen differs from what the server
  // holds. The 428 prompt uses it: it says what would be LOST, and a field put back to
  // its saved value while its (now refused) save was still in flight loses nothing.
  var dirtyOnly = !!(opts && opts.dirtyOnly)
  var out = []
  ;(scope ? scope.rows : allRecords).forEach(function (rec) {
    if (unguarded && isGuardedRec(rec)) {
      return
    }
    Object.keys(rec.entries).forEach(function (path) {
      var entry = rec.entries[path]
      var listed = dirtyOnly
        ? (withDrafts || !rec.draft) && isDirty(rec, path)
        : isPendingEntry(rec, entry, path, withDrafts)
      // A decision group's CLEAN member (the date of a status-only change) rides in the
      // same command but is not one of the user's changes: it is not listed.
      if (listed && decisionSpecFor(rec, path) && !isDirty(rec, path) && !entry.sentChanged) {
        listed = false
      }
      if (listed) {
        out.push({
          meta: rowMeta(rec),
          path: path,
          yours: cloneValue(getPath(rec.row, path)),
          inFlight: !!entry.inFlight,
          failed: !!entry.networkFailed
        })
      }
    })
  })
  return out
}

/**
 * A step component unmounting with a text field still focused gets no blur:
 * release every focus in the scope so nothing stays held for good.
 */
export function releaseFocusedInScope (scope) {
  if (!scope) {
    return
  }
  scope.rows.forEach(function (rec) {
    Object.keys(rec.entries).forEach(function (path) {
      var entry = rec.entries[path]
      if (entry && entry.focused) {
        entry.focused = false
        releaseIfIdle(rec, path)
      }
    })
  })
  refreshScope(scope)
  bump()
}

/* ------------------------------------------------------------------------
   The request body: guard G-BASE (R2.5, R2.6)
------------------------------------------------------------------------ */

// Dates go out as Y-m-d (XOL-595: the server normalises, but a retry in another
// format must not be what decides a conflict). `targetDate` / `actualDate` are
// DATE columns; through the app clock, never a new parse (XOL-414).
function wireValue (path, value) {
  var last = lastSegment(path)
  if ((last === 'targetDate' || last === 'actualDate') && !isEmptyLeaf(value)) {
    var m = toAppTime(value)
    return m === null ? cloneValue(value) : m.format(DATE_SQL_FORMAT)
  }
  return cloneValue(value)
}

/**
 * The ONLY place a content request body is built, and the only place `base` and
 * `contentVersion` are computed.
 *
 *   { contentVersion: <the row's lastAdoptedVersion>,
 *     fields: { <path>: <value> },
 *     base:   { <path>: <the FIELD's own baseVersion, an integer> } }
 *
 * Exactly these three top-level keys, one path (never an empty `fields`, never
 * two overlapping paths: XOL-595 A5 / A7). Never `updated_by`, never a
 * server-owned or structural key, not even an echo of its stored value (A6: the
 * API answers that with 422). Returns null when the path may not be sent or the
 * row has no version.
 *
 * @param {Object} rec    the row record
 * @param {String} path
 * @param {*}      value  the value to send
 * @param {Object} entry  the field's entry (defaults to rec.entries[path])
 */
export function buildFieldBody (rec, path, value, entry) {
  entry = entry || rec.entries[path]
  var actionType = rec.kind === 'action' ? rec.row.actionType : undefined
  if (!entry || !checkWritablePath(rec.kind, path, actionType).ok) {
    return null
  }
  if (typeof rec.lastAdoptedVersion !== 'number' || typeof entry.baseVersion !== 'number') {
    return null
  }
  var fields = {}
  var base = {}
  fields[path] = wireValue(path, value)
  base[path] = entry.baseVersion
  return { contentVersion: rec.lastAdoptedVersion, fields: fields, base: base }
}

/* ------------------------------------------------------------------------
   Leaves, not parents (XOL-595 A4)
------------------------------------------------------------------------ */

function collectLeaves (prefix, before, after, out) {
  if (isPlainObject(after) && (before === undefined || before === null || isPlainObject(before))) {
    Object.keys(after).forEach(function (key) {
      var child = prefix + '.' + key
      if (!isValidPath(child)) {
        console.error('XOL-596: skipping a key that is not a valid path segment', child)
        return
      }
      collectLeaves(child, before ? before[key] : undefined, after[key], out)
    })
    return
  }
  // A leaf, or an array (whole at its own path).
  if (after === undefined) {
    return // v3 has no deletes
  }
  for (var i = 0; i < SERVER_OWNED_PATHS.length; i++) {
    if (pathStartsWith(prefix, SERVER_OWNED_PATHS[i])) {
      return
    }
  }
  if (!valuesEqual(prefix, before, after)) {
    out.push({ path: prefix, value: cloneValue(after) })
  }
}

/**
 * Turn a changed object into LEAF paths. The API refuses a write to a parent of
 * a protected path (`state` whole, `state.step3`, `state.step5` on an RTB), so a
 * component that edited an object sends each changed leaf on its own:
 *
 *   changedLeafPaths('state', {step3: {docType: 'a'}}, {step3: {docType: 'b'}})
 *     -> [{path: 'state.step3.docType', value: 'b'}]
 *
 * Plain objects are walked; arrays and scalars are leaves (an array is a whole
 * value at its own path); unchanged leaves, server-owned paths and deletions
 * yield nothing.
 *
 * @return {Array} [{ path, value }]
 */
export function changedLeafPaths (basePath, before, after) {
  var out = []
  if (!isValidPath(basePath)) {
    return out
  }
  collectLeaves(basePath, before, after, out)
  return out
}

/**
 * Mark every leaf that changed under `basePath` (compared with the last server
 * value) as changed, exactly as `fieldChanged` would for each. For a template
 * that edits an object (step 4's `state`, say) instead of one leaf. Returns the
 * leaf paths.
 */
export function fieldsChangedUnder (row, basePath) {
  var rec = recordFor(row)
  if (!rec) {
    return []
  }
  var leaves = changedLeafPaths(basePath, getPath(rec.shadow, basePath), getPath(row, basePath))
  leaves.forEach(function (leaf) {
    fieldChanged(row, leaf.path)
  })
  return leaves.map(function (leaf) {
    return leaf.path
  })
}

/* ------------------------------------------------------------------------
   Adopt: guard G-MERGE, the per-field hold (R2.7, R2.19, R2.22)
------------------------------------------------------------------------ */

/** A sequence number for a request, taken when it is ISSUED. */
export function nextSeq () {
  return ++seqCounter
}

/** Was a response to a request issued at `seq` overtaken for this row? */
export function isOutdated (row, seq) {
  var rec = getRecord(row)
  return !!rec && typeof seq === 'number' && seq <= rec.lastAdoptedSeq
}

/** Record that a response issued at `seq` was adopted into this row. */
export function noteAdopted (row, seq) {
  var rec = recordFor(row)
  if (rec && typeof seq === 'number' && seq > rec.lastAdoptedSeq) {
    rec.lastAdoptedSeq = seq
  }
}

// contract v3 (C11): fieldVersions[path] = {v, by, org, at}; accept a bare
// number too, which is the draft shape.
function readFieldVersion (fv) {
  if (fv === undefined || fv === null) {
    return null
  }
  if (typeof fv === 'number') {
    return { v: fv, by: null, org: null, at: null }
  }
  return {
    v: toVersion(fv.v) === null ? 0 : toVersion(fv.v),
    by: fv.by || null,
    org: fv.org || null,
    at: fv.at || null
  }
}

// The API's conflict check compares the path with every recorded path equal to,
// above or beneath it; the cue uses the same relation. Highest version wins,
// the exact path on a tie.
function relatedFieldVersion (fieldVersions, path) {
  if (!isPlainObject(fieldVersions)) {
    return null
  }
  var best = null
  var bestPath = null
  var keys = Object.keys(fieldVersions)
  for (var i = 0; i < keys.length; i++) {
    if (!pathsRelated(keys[i], path)) {
      continue
    }
    var fv = readFieldVersion(fieldVersions[keys[i]])
    if (best === null || fv.v > best.v || (fv.v === best.v && keys[i] === path)) {
      best = fv
      bestPath = keys[i]
    }
  }
  if (best) {
    best.path = bestPath
  }
  return best
}

// `state` and `emails` arrive whole from the server (R2.22), but the components
// hold aliases into them (`rowData.state.step3`, `emails.owner.additional`, a v-model on
// `state.deposit`), so they are merged IN PLACE: an object keeps its identity
// and gets the server's keys (a local key the server lacks is removed), an array
// keeps its identity and takes the server's items.
function mergeInPlace (local, server) {
  if (Array.isArray(local) && Array.isArray(server)) {
    local.splice.apply(local, [0, local.length].concat(cloneValue(server)))
    return
  }
  Object.keys(local).forEach(function (k) {
    if (!Object.prototype.hasOwnProperty.call(server, k)) {
      Vue.delete(local, k)
    }
  })
  Object.keys(server).forEach(function (k) {
    var l = local[k]
    var v = server[k]
    if ((isPlainObject(l) && isPlainObject(v)) || (Array.isArray(l) && Array.isArray(v))) {
      mergeInPlace(l, v)
    } else {
      Vue.set(local, k, cloneValue(v))
    }
  })
}

var IN_PLACE_COLUMNS = ['state', 'emails']

/**
 * Set a path to a value the way a component that aliases the old value can live
 * with: an array or object that is already there keeps its IDENTITY and takes the
 * new items (mergeInPlace), so `equipment: this.rowData.state.step2.equipment`
 * and `emails.owner.additional` held by a component never go stale. Anything else is a plain
 * setPath. Every write of a list or object to a row's content goes through here
 * (a held value put back after an adopt, a parked value applied on release, the
 * server's value after a conflict).
 */
export function assignPath (obj, path, value) {
  var cur = getPath(obj, path)
  if (isObjectLike(cur) && isObjectLike(value) && Array.isArray(cur) === Array.isArray(value)) {
    if (cur !== value) {
      mergeInPlace(cur, value)
    }
    return true
  }
  return setPath(obj, path, value)
}

// Put a HELD value back after an adopt. The adopt's in-place merge has just
// spliced the server's items into the very array the component aliases; put the
// user's items back into that SAME object (`h.ref`, taken before the merge) and,
// if the merge detached it (the server's state had no such key), attach it again.
function restoreHeld (target, h) {
  var live = h.ref
  if (isObjectLike(live) && isObjectLike(h.local) && Array.isArray(live) === Array.isArray(h.local)) {
    mergeInPlace(live, h.local)
    if (getPath(target, h.path) !== live) {
      setPath(target, h.path, live)
    }
    return
  }
  setPath(target, h.path, h.local)
}

function indexById (list, id) {
  for (var i = 0; i < list.length; i++) {
    if (list[i] && Number(list[i].id) === Number(id)) {
      return i
    }
  }
  return -1
}

// `steps` is never replaced as an array. Each server row is merged into the
// local row with the same id (one object per row); an unmatched server row is
// appended; a local row the response does not carry is kept.
function mergeSteps (target, serverSteps, opts) {
  if (!Array.isArray(target.steps)) {
    Vue.set(target, 'steps', serverSteps.slice())
    return
  }
  for (var i = 0; i < serverSteps.length; i++) {
    var sr = serverSteps[i]
    if (!isObjectLike(sr)) {
      continue
    }
    var at = indexById(target.steps, sr.id)
    if (at === -1) {
      target.steps.push(sr)
    } else {
      adoptRow(target.steps[at], sr, { kind: 'step', seq: opts.seq, command: opts.command })
    }
  }
}

// Who decided, and when, for the "changed underneath you" cue of a decision path:
// the server row's `approved_by` and `decided_at` (the day, `approved_at`, when a legacy
// row has no moment). Step 3's group lives on the ACTION row, so the party-1 step 3 row
// inside the response says it.
function decisionCueSource (spec, serverRow) {
  var src = serverRow
  if (spec.kind === 'step3') {
    src = null
    var steps = isObjectLike(serverRow) && Array.isArray(serverRow.steps) ? serverRow.steps : []
    for (var i = 0; i < steps.length; i++) {
      if (steps[i] && spec.commandRow && Number(steps[i].id) === Number(spec.commandRow.id)) {
        src = steps[i]
      }
    }
  }
  if (!src) {
    return { by: null, at: null }
  }
  return { by: src.approved_by || null, at: src.decided_at || src.approved_at || null }
}

/**
 * Adopt a server row into the ONE local object for that row, in place.
 *
 *  1. A response issued before the last adopted one for this row is ignored
 *     (`seq`), and so is one older than the row's `lastAdoptedVersion`.
 *  2. The local value of every HELD path is remembered (held = focused, dirty,
 *     queued or in flight).
 *  3. The server row is merged by KEY (G-MERGE): every key present is set
 *     (Vue.set), a key that is absent is left alone (a lean response omits
 *     `site`, `organisation`, `workflow`, `backhaul`, ...), the row object is
 *     never replaced and `steps` is merged by id. `state` and `emails` arrive
 *     whole, at the column level.
 *  4. The held paths' local values are put back, and the server's value for
 *     each is parked in `deferred` (applied when the field is released). When
 *     the server changed the path after this field's base and the value differs
 *     from the base value, the field gets a cue (R2.19).
 *  5. `lastAdoptedVersion` / `lastAdoptedSeq` move forward, and the shadow is
 *     refreshed. A held field keeps its OWN `baseVersion` (G-BASE).
 *
 * @param {Object} target     the local row
 * @param {Object} serverRow  the row from a response
 * @param {Object} opts       { kind, seq, command }. `command: true` marks a COMMAND's
 *                            own response (or its 409 body): its owned keys are set
 *                            whatever its seq and the row's floor moves up.
 * @return {Object} { adopted: Boolean, reason?, held: [paths] }
 */
export function adoptRow (target, serverRow, opts) {
  opts = opts || {}
  if (!isObjectLike(target) || !isObjectLike(serverRow)) {
    return { adopted: false, reason: 'empty', held: [] }
  }
  var rec = recordFor(target, opts.kind)
  var seq = opts.seq
  var isCommand = !!opts.command
  var version = toVersion(serverRow.contentVersion)

  // 2. remember what the user is holding (held = focused, dirty, queued or in
  // flight). FIX 1 (XOL-596, decision fields): captured BEFORE rule (a), which writes
  // the display of every owned key. Taken after it, a command's response (and its
  // 409 body) overwrote a decision picked again during its own flight, and the
  // Complete Step tick held by `holdOwned` when ANOTHER command's response landed.
  var held = []
  Object.keys(rec.entries).forEach(function (path) {
    if (isHeld(rec, path)) {
      held.push({ path: path, local: cloneValue(getPath(target, path)), ref: getPath(target, path) })
    }
  })

  // What a running command is deciding keeps its local value too (holdOwned).
  var heldOwned = Object.keys(rec.heldOwned).map(function (k) {
    return { path: k, local: cloneValue(getPath(target, k)) }
  })

  // (a) A command's own response decides the owned keys, even when the rest of
  // it turns out to be outdated. The BASELINE takes them; a value the user holds
  // goes back on the display below (and the server's is parked in `deferred`).
  if (isCommand) {
    applyOwnedFromCommand(rec, target, serverRow)
    rec.ownedFloor = nextSeq()
  }

  // The held values of a response that is not merged after all (outdated): rule (a)
  // has already written the owned keys, so what the user holds goes back.
  var putBackHeld = function () {
    if (!isCommand) {
      return
    }
    heldOwned.forEach(function (h) {
      if (h.local !== undefined) {
        assignPath(target, h.path, h.local)
      }
    })
    held.forEach(function (h) {
      if (decisionSpecFor(rec, h.path)) {
        restoreHeld(target, h)
      }
    })
  }

  // (b) + (c) Any other adopt leaves the owned keys alone while a command is in
  // flight on the row, and when it was issued before the last command's answer.
  var skipOwned = !isCommand && (rec.commandsInFlight > 0 || (typeof seq === 'number' && seq < rec.ownedFloor))
  var ownedList = ownedKeysFor(rec.kind)
  // contentVersion only ever goes up per row, so a response with a STRICTLY
  // higher one is newer than anything adopted, whatever its seq: two saves of
  // one flush can be handled by the server in the other order, and the lower-seq
  // response is then the newer snapshot. Commands do not bump the version, so
  // an equal version still falls back to the seq (a late GET).
  var newerVersion = version !== null && rec.lastAdoptedVersion !== null && version > rec.lastAdoptedVersion
  if (typeof seq === 'number' && seq <= rec.lastAdoptedSeq && !newerVersion) {
    putBackHeld()
    return { adopted: false, reason: 'outdated', held: [] }
  }
  if (version !== null && rec.lastAdoptedVersion !== null && version < rec.lastAdoptedVersion) {
    putBackHeld()
    return { adopted: false, reason: 'older-version', held: [] }
  }

  // The legal tick lives inside `state`, which is merged in place: keep it too.
  var legalKeep = skipOwned && rec.kind === 'action' && hasPath(target, LEGAL_PATH) ? cloneValue(getPath(target, LEGAL_PATH)) : undefined

  // A field that is not held takes the server value below. Its entry, if it has
  // one (a change that was reverted, say), would then read as dirty against its
  // stale base value, so it goes.
  Object.keys(rec.entries).forEach(function (path) {
    if (!isHeld(rec, path)) {
      delete rec.entries[path]
      delete rec.deferred[path]
    }
  })

  // 3. merge by key. Object values are cloned, so the local row never aliases
  // the response (the put-back below would otherwise write into the response).
  var serverValues = {}
  held.forEach(function (h) {
    if (hasPath(serverRow, h.path)) {
      serverValues[h.path] = cloneValue(getPath(serverRow, h.path))
    }
  })

  Object.keys(serverRow).forEach(function (key) {
    if (skipOwned && ownedList.indexOf(key) !== -1) {
      return
    }
    if (key === 'steps' && rec.kind === 'action' && Array.isArray(serverRow.steps)) {
      mergeSteps(target, serverRow.steps, { seq: seq, command: isCommand })
    } else if (IN_PLACE_COLUMNS.indexOf(key) !== -1 && isPlainObject(target[key]) &&
      (isPlainObject(serverRow[key]) || serverRow[key] === null)) {
      // A `state` / `emails` of null from the server must not null the object the
      // components read (`step.state.reviseTP`): the local object stays, and takes
      // the server's (empty) content in place.
      mergeInPlace(target[key], serverRow[key] || {})
    } else {
      Vue.set(target, key, isObjectLike(serverRow[key]) ? cloneValue(serverRow[key]) : serverRow[key])
    }
  })

  heldOwned.forEach(function (h) {
    if (h.local !== undefined) {
      assignPath(target, h.path, h.local)
    }
  })
  if (legalKeep !== undefined) {
    assignPath(target, LEGAL_PATH, legalKeep)
  }

  // 4. put the held values back, park the server's, raise cues
  var heldPaths = []
  held.forEach(function (h) {
    var entry = rec.entries[h.path]
    heldPaths.push(h.path)
    restoreHeld(target, h)
    if (!Object.prototype.hasOwnProperty.call(serverValues, h.path)) {
      return
    }
    var decision = decisionSpecFor(rec, h.path)
    // FIX 2: an adopt that is not allowed to set the owned keys (a command is in
    // flight, or the snapshot was issued before the last command's answer) is a
    // possibly STALE snapshot of a decision: parking it would let `dropEntry` apply
    // it later. A decision path parks nothing and raises no cue then.
    if (decision && skipOwned) {
      return
    }
    var serverValue = serverValues[h.path]
    rec.deferred[h.path] = { value: serverValue, version: version !== null ? version : rec.lastAdoptedVersion }

    // C-11 for a decision path. `fieldVersions` does not cover the owned keys
    // (commands bump no contentVersion), so the trigger is the baseline: the server's
    // value differs from the base the user saw and from what they hold. Raised for
    // every path of a held group (UF-D1: the group's save is refused either way). A
    // command's own response never raises it: the server re-stamping the date of our
    // status-only decision is not someone else's change.
    if (decision) {
      if (isCommand || (entry.inFlight && valuesEqual(h.path, serverValue, entry.inFlightValue))) {
        entry.cue = null
        return
      }
      if (!valuesEqual(h.path, serverValue, entry.value) && !valuesEqual(h.path, serverValue, h.local)) {
        var who = decisionCueSource(decision, serverRow)
        entry.cue = {
          path: h.path,
          serverValue: serverValue,
          changedAtVersion: version,
          by: who.by,
          org: null,
          at: who.at
        }
      } else if (entry.cue && (valuesEqual(h.path, serverValue, entry.value) || valuesEqual(h.path, serverValue, h.local))) {
        entry.cue = null
      }
      return
    }

    // Our own write is not "someone else's change": while this field's save is
    // in flight, a server value equal to the SENT value is that write coming
    // back (in its own response or in another request's snapshot). A different
    // value is someone else's, even in the response to our own save: the API
    // reads that snapshot after commit, outside the lock, so another user can
    // write the same path in between.
    if (entry.inFlight && valuesEqual(h.path, serverValue, entry.inFlightValue)) {
      entry.cue = null
      return
    }
    var fv = relatedFieldVersion(serverRow.fieldVersions, h.path)
    var changedAt = fv ? fv.v : 0
    var base = typeof entry.baseVersion === 'number' ? entry.baseVersion : 0
    if (changedAt > base && !valuesEqual(h.path, serverValue, entry.value) && !valuesEqual(h.path, serverValue, h.local)) {
      entry.cue = {
        path: h.path,
        serverValue: serverValue,
        changedAtVersion: changedAt,
        by: fv.by,
        org: fv.org,
        at: fv.at
      }
    } else if (entry.cue && (valuesEqual(h.path, serverValue, entry.value) || valuesEqual(h.path, serverValue, h.local))) {
      entry.cue = null
    }
  })

  // 5. bookkeeping
  if (version !== null) {
    rec.lastAdoptedVersion = version
  }
  if (typeof seq === 'number' && seq > rec.lastAdoptedSeq) {
    rec.lastAdoptedSeq = seq
  }
  rec.adoptedAt = Date.now()
  var keys = contentKeysFor(rec.kind)
  for (var i = 0; i < keys.length; i++) {
    if (serverRow[keys[i]] !== undefined) {
      rec.shadow[keys[i]] = cloneValue(serverRow[keys[i]])
      // Only a column the response CARRIED is now at this version.
      if (version !== null) {
        rec.columnVersion[keys[i]] = version
      }
    }
  }
  held.forEach(function (h) {
    // A decision path is server-owned: never in the content shadow.
    if (!decisionSpecFor(rec, h.path)) {
      setPath(rec.shadow, h.path, cloneValue(rec.entries[h.path].value))
    }
  })
  // The workflow-owned columns this response carried are the new server truth.
  if (!skipOwned) {
    var owned = ownedKeysFor(rec.kind)
    for (var o = 0; o < owned.length; o++) {
      if (serverRow[owned[o]] !== undefined) {
        rec.workflow[owned[o]] = cloneValue(serverRow[owned[o]])
      }
    }
    if (rec.kind === 'action' && hasPath(serverRow, LEGAL_PATH)) {
      rec.workflow[LEGAL_PATH] = cloneValue(getPath(serverRow, LEGAL_PATH))
    }
  }
  refreshScope(rec.scope)
  bump()
  return { adopted: true, held: heldPaths }
}

/**
 * After a 2xx for `path`: does the returned row hold the value we SENT, and
 * which version should the field's base move to?
 *
 * The API reads the response snapshot after commit, outside the row lock, so
 * another user can write the same path in between: our commit at v8, theirs at
 * v9, our response carrying v9 and THEIR value. The base may move to the
 * response's version only when the value at the path is the one we sent. When it
 * is not, the base goes to one below the newest related version (never below the
 * old base), so the next save conflicts (G-BASE).
 *
 * @return {Object} { confirmed: Boolean, version: Number|undefined }
 */
export function confirmSaved (rec, path, sentValue, savedRow) {
  var version = savedRow ? toVersion(savedRow.contentVersion) : null
  var entry = rec.entries[path]
  var carried = !!savedRow && hasPath(savedRow, path)
  if (!carried || valuesEqual(path, getPath(savedRow, path), sentValue)) {
    return { confirmed: true, version: version === null ? undefined : version }
  }
  var fv = relatedFieldVersion(savedRow.fieldVersions, path)
  var old = entry && typeof entry.baseVersion === 'number' ? entry.baseVersion : 0
  return { confirmed: false, version: fv ? Math.max(old, fv.v - 1) : old }
}

/* ------------------------------------------------------------------------
   The per-row queue (R2.8)
------------------------------------------------------------------------ */

/**
 * Run `task` after everything already queued under `key`. Never rejects: a task
 * that throws or rejects resolves undefined (and is logged). Returns the task's
 * result.
 */
export function enqueue (key, task) {
  var tail = queues[key] || Promise.resolve()
  var run = tail.then(function () {
    try {
      return task()
    } catch (e) {
      console.error('requestRowSync: queued task threw', key, e)
      return undefined
    }
  }).catch(function (e) {
    console.error('requestRowSync: queued task failed', key, e)
    return undefined
  })
  var mine = run.then(function () {}, function () {})
  queues[key] = mine
  mine.then(function () {
    if (queues[key] === mine) {
      delete queues[key]
    }
  })
  return run
}

/**
 * Run `task` once every one of `keys` is idle, and hold all of them while it
 * runs, so anything queued afterwards waits for it. Commands use this: they
 * wait on the involved rows' saves and block the next ones.
 */
export function enqueueAll (keys, task) {
  var tails = keys.map(function (k) {
    return queues[k] || Promise.resolve()
  })
  var run = Promise.all(tails).then(function () {
    try {
      return task()
    } catch (e) {
      console.error('requestRowSync: queued task threw', keys, e)
      return undefined
    }
  }).catch(function (e) {
    console.error('requestRowSync: queued task failed', keys, e)
    return undefined
  })
  var mine = run.then(function () {}, function () {})
  keys.forEach(function (k) {
    queues[k] = mine
  })
  mine.then(function () {
    keys.forEach(function (k) {
      if (queues[k] === mine) {
        delete queues[k]
      }
    })
  })
  return run
}

/** Resolves when everything queued under `keys` (default: every key) is done. */
export function whenIdle (keys) {
  var list = keys || Object.keys(queues)
  return Promise.all(list.map(function (k) {
    return queues[k] || Promise.resolve()
  })).then(function () {})
}

/* ------------------------------------------------------------------------
   Bursts: one pause-flush = one popup (R2.13)
------------------------------------------------------------------------ */

function newBurst (opts) {
  return {
    id: ++burstCounter,
    scope: opts.scope || null,
    reason: opts.reason || 'flush',
    system: !!opts.system,
    silent: !!opts.silent,
    // The ordering rule (UX): { kind: 'reject' | 'complete' | 'email', rtx }. A
    // failed save of such a flush gets one more footer: the command or e-mail
    // that was NOT sent.
    ordering: opts.ordering || null,
    results: [],
    settled: false
  }
}

/**
 * { saved: [outcomes], failed: [outcomes], skipped: [outcomes], ok }
 * of a burst (or of any list of outcomes).
 */
export function summariseBurst (burstOrResults) {
  var results = Array.isArray(burstOrResults) ? burstOrResults : burstOrResults.results
  var out = { saved: [], failed: [], skipped: [], ok: true }
  for (var i = 0; i < results.length; i++) {
    var r = results[i]
    if (!r) {
      continue
    }
    if (r.status === 'saved') {
      out.saved.push(r)
    } else if (OK_STATUSES.indexOf(r.status) !== -1) {
      out.skipped.push(r)
    } else {
      out.failed.push(r)
      out.ok = false
    }
  }
  return out
}

/**
 * Register the function that turns a settled burst into a popup. It receives
 * (burst, summary). Slice 2 wires the real one; with none registered a burst
 * settles silently. Survives resetSyncState (it is app wiring, not session
 * state).
 */
export function setBurstReporter (fn) {
  burstReporter = typeof fn === 'function' ? fn : null
}

function finishBurst (burst) {
  if (burst.settled) {
    return summariseBurst(burst)
  }
  burst.settled = true
  var summary = summariseBurst(burst)
  var sent = summary.saved.length + summary.failed.length
  // Nothing was sent: no popup. A system save (auto-filled recipients) is
  // silent on success; its failures still report.
  if (sent === 0 || burst.silent || (burst.system && summary.ok)) {
    return summary
  }
  if (burstReporter) {
    try {
      burstReporter(burst, summary)
    } catch (e) {
      console.error('requestRowSync: burst reporter threw', e)
    }
  }
  return summary
}

/* ------------------------------------------------------------------------
   Saving: the queued task per path (R2.8)
------------------------------------------------------------------------ */

/**
 * What a notice needs to name a row: { kind, id, rtx, actionId, stepNo, partyNo,
 * stepName }. Read from the row itself (step rows carry actionType and stepName).
 */
export function rowMeta (rec) {
  var row = rec.row || {}
  return {
    kind: rec.kind,
    id: rec.id,
    rtx: row.actionType,
    actionId: rec.kind === 'action' ? row.id : row.action_id,
    stepNo: row.stepNo,
    partyNo: row.partyNo,
    stepName: row.stepName,
    // The party's short name (a step row's `org`), and the telco's name (an
    // action's `organisation`): labels for the notices.
    orgShort: row.org ? (row.org.shortName || row.org.name) : null,
    // 'actionDetails' | 'progressStep' | null: an EditPane form's row is named
    // after the form, not after a drawer step (UX C-4).
    form: rec.form || null,
    telcoName: rec.kind === 'action' && row.organisation ? row.organisation.name : null,
    // The backhaul provider's short name (an RTB's `providerShortName`): the label of
    // its Emails Update lists.
    providerName: rec.kind === 'action' && row.providerShortName ? row.providerShortName : null
  }
}

/* ------------------------------------------------------------------------
   Which step a path belongs to (XOL-596, completed steps are read-only)
   The same rule as the API's CompletedStepRule::stepNoForPath:
     - every path of a step row belongs to that row's stepNo (either party);
     - an action path belongs to step N when it is `state.stepN` or lies beneath it;
     - General Remarks (`remarks` on the action) belongs to STEP 1 when it is edited
       from the request workflow (Carlos, 2026-10-14: "General remarks are per step"),
       and to no step when it is edited from Site -> Actions (ActionDetails, whose row
       is registered with form 'actionDetails');
     - every other action path (emails.*, summary, priority, targetDate, actualDate,
       actionBy) belongs to no step and is never frozen.
------------------------------------------------------------------------ */

var ACTION_STEP_PATH = /^state\.step([0-9]+)(\.|$)/
var FROZEN_TYPES = ['RTS', 'RTU', 'RTB', 'RTD']

/**
 * The step number a path of record `rec` belongs to, or null. `rec` needs `kind`,
 * `row` ({stepNo | actionType}) and, for an action, `form`.
 */
export function stepNoOfPath (rec, path) {
  if (!rec) {
    return null
  }

  var row = rec.row || {}

  if (rec.kind === 'step') {
    var n = Number(row.stepNo)
    return n > 0 ? n : null
  }

  var m = ACTION_STEP_PATH.exec(String(path))

  if (m) {
    return Number(m[1])
  }

  if (path === 'remarks' && rec.kind === 'action' && rec.form !== 'actionDetails' &&
    FROZEN_TYPES.indexOf(String(row.actionType || '').toUpperCase()) !== -1) {
    return 1
  }

  return null
}

/**
 * The step a settled outcome (a failed field save, a decision) belongs to, or null.
 * A 403 step_completed names its own step (`stepNo`, from the body); otherwise the
 * outcome's row and path decide.
 */
export function stepNoOfOutcome (o) {
  if (!o) {
    return null
  }

  if (o.code === 'step_completed' && o.stepNo !== undefined && o.stepNo !== null) {
    return Number(o.stepNo)
  }

  if (o.decisionStepId !== undefined) {
    var rec = findRecord('step', o.decisionStepId)

    return rec && rec.row ? Number(rec.row.stepNo) : null
  }

  var m = o.meta || {}

  return stepNoOfPath({ kind: o.kind, form: m.form, row: { stepNo: m.stepNo, actionType: m.rtx } }, o.path)
}

function outcomeFor (rec, path, status, extra) {
  var out = { status: status, path: path, kind: rec.kind, id: rec.id, meta: rowMeta(rec) }
  if (extra) {
    Object.keys(extra).forEach(function (k) {
      out[k] = extra[k]
    })
  }
  return out
}

// The task body. The payload is computed WHEN IT RUNS, from the then-current
// value, and skipped when the path is no longer dirty (an earlier task saved it,
// or it was changed back).
function runFieldSave (rec, path, burst, opts) {
  var entry = rec.entries[path]
  if (entry) {
    entry.queued = false
    entry.queuedPromise = null
  }
  var outcome

  // A save that another path already STARTED (the page-leave keepalive PUT) is
  // this task: its outcome is handled once, and no duplicate request goes out.
  if (entry && entry.inFlight && entry.inFlightPromise) {
    return entry.inFlightPromise
  }

  if (isTearingDown()) {
    outcome = outcomeFor(rec, path, 'teardown')
    burst.results.push(outcome)
    return Promise.resolve(outcome)
  }
  if (!entry || !isDirty(rec, path)) {
    releaseIfIdle(rec, path)
    refreshScope(rec.scope)
    outcome = outcomeFor(rec, path, 'skipped')
    burst.results.push(outcome)
    return Promise.resolve(outcome)
  }
  if (typeof rec.sender !== 'function') {
    console.error('requestRowSync: no sender registered for', rec.queueKey)
    outcome = outcomeFor(rec, path, 'no-sender')
    entry.lastOutcome = 'no-sender'
    burst.results.push(outcome)
    refreshScope(rec.scope)
    return Promise.resolve(outcome)
  }

  var req = {
    rec: rec,
    kind: rec.kind,
    id: rec.id,
    path: path,
    value: cloneValue(getPath(rec.row, path)),
    base: entry.baseVersion,
    contentVersion: rec.lastAdoptedVersion,
    seq: nextSeq(),
    burst: burst,
    keepalive: !!(opts && opts.keepalive),
    system: !!(opts && opts.system),
    opsCount: (rec.ops[path] || []).length
  }
  entry.inFlight = true
  entry.inFlightSeq = req.seq
  entry.inFlightValue = cloneValue(req.value)
  refreshScope(rec.scope)
  bump()

  // A keepalive save (page leave) must START inside the beforeunload / pagehide
  // handler, not a microtask later; every other save starts on the next tick.
  var started
  if (req.keepalive) {
    try {
      started = Promise.resolve(rec.sender(req))
    } catch (e) {
      started = Promise.reject(e)
    }
  } else {
    started = Promise.resolve().then(function () {
      return rec.sender(req)
    })
  }

  var inFlight = started.then(function (result) {
    return result
  }, function (err) {
    console.error('requestRowSync: sender threw', rec.queueKey, path, err)
    return outcomeFor(rec, path, 'error', { err: err })
  }).then(function (result) {
    var res = result || outcomeFor(rec, path, 'failed')
    res.path = res.path || path
    res.kind = res.kind || rec.kind
    res.id = res.id === undefined ? rec.id : res.id
    res.meta = res.meta || rowMeta(rec)
    if (res.value === undefined) {
      res.value = req.value
    }
    settleOutcome(rec, path, req, res)
    burst.results.push(res)
    refreshScope(rec.scope)
    bump()
    return res
  })
  // A flush that meets this field mid-flight awaits it and counts its outcome.
  entry.inFlightPromise = inFlight
  return inFlight
}

// What the entry does after the sender answered. The sender has already adopted
// whatever rows came back and (for a conflict) taken the server value.
function settleOutcome (rec, path, req, res) {
  var entry = rec.entries[path]
  if (!entry) {
    return
  }
  // A saved value is no longer a pending record, unless the user changed the field again
  // while the save was in flight (that change is recorded and dirty against the new base).
  if (res.status === 'saved') {
    entry.recorded = entry.inFlightValue !== undefined && !valuesEqual(path, getPath(rec.row, path), entry.inFlightValue)
  }
  entry.inFlight = false
  entry.inFlightSeq = null
  entry.inFlightValue = undefined
  entry.inFlightPromise = null
  entry.lastOutcome = res.status
  if (res.status === 'saved') {
    // R2.5: the entry becomes {sent value, the response's version}; it stays
    // held when still focused or changed again during the flight. A re-applied
    // list (R2.9) sent a value other than the one the flush started with.
    var sent = res.sentValue !== undefined ? res.sentValue : req.value
    // The list ops that went out with this save are history now.
    if (rec.ops[path] && typeof req.opsCount === 'number') {
      rec.ops[path] = rec.ops[path].slice(req.opsCount)
    }
    entry.value = cloneValue(sent)
    entry.networkFailed = false
    if (res.confirmed === false) {
      // The response snapshot does not hold our value: another user wrote this
      // path between our commit and the snapshot (the API reads it outside the
      // lock). The base moves to the last version BEFORE theirs (the sender
      // computed it), never to the response's, so the next save conflicts
      // instead of silently overwriting them, and the cue the adopt raised stays.
      if (typeof res.version === 'number') {
        entry.baseVersion = res.version
      }
    } else {
      if (typeof res.version === 'number') {
        entry.baseVersion = res.version
        // The response may have been ignored by the adopt (an older snapshot of
        // the row): park OUR value, so releasing does not apply an older one.
        var parked = rec.deferred[path]
        if (!parked || res.version >= parked.version) {
          rec.deferred[path] = { value: cloneValue(sent), version: res.version }
        }
      }
      entry.cue = null
    }
    releaseIfIdle(rec, path)
  } else if (res.status === 'network') {
    entry.networkFailed = true
  } else if (res.status === 'blocked') {
    abandonEntry(rec, path)
  }
}

function enqueueFieldSave (rec, path, burst, opts) {
  var entry = rec.entries[path]
  // At most one queued task per path: join it. It builds its body when it runs,
  // so it carries the latest value.
  if (entry.queued && entry.queuedPromise) {
    return entry.queuedPromise
  }
  entry.queued = true
  var p = enqueue(rec.queueKey, function () {
    return runFieldSave(rec, path, burst, opts)
  })
  entry.queuedPromise = p
  return p
}

/* ------------------------------------------------------------------------
   Decision groups: one queued task, one approval command (XOL-596 D3-D5)
------------------------------------------------------------------------ */

function decisionEntries (rec) {
  var spec = rec.decision
  if (!spec) {
    return []
  }
  return spec.paths.map(function (p) {
    return rec.entries[p]
  }).filter(function (e) {
    return !!e
  })
}

function decisionDirty (rec) {
  return !!rec.decision && rec.decision.paths.some(function (p) {
    return isDirty(rec, p)
  })
}

// The record's group has a save running or waiting.
function decisionBusy (rec) {
  return decisionEntries(rec).some(function (e) {
    return e.inFlight || e.queued
  })
}

// At most one queued task per group: the entries share it. It builds its request
// when it runs, so it carries the latest values. It waits behind every save already
// queued on the command's rows (content first, D4), and holds them while it runs.
function enqueueDecisionSave (rec, burst, opts) {
  var entries = decisionEntries(rec)
  var queued = entries.filter(function (e) {
    return e.queued && e.queuedPromise
  })[0]
  if (queued) {
    return queued.queuedPromise
  }
  var keys = []
  rec.decision.rows.forEach(function (row) {
    var r = recordFor(row)
    if (r && keys.indexOf(r.queueKey) === -1) {
      keys.push(r.queueKey)
    }
  })
  if (keys.indexOf(rec.queueKey) === -1) {
    keys.push(rec.queueKey)
  }
  entries.forEach(function (e) {
    e.queued = true
  })
  var p = enqueueAll(keys, function () {
    return runDecisionSave(rec, burst, opts)
  })
  entries.forEach(function (e) {
    e.queuedPromise = p
  })
  return p
}

// The task body. The request is built WHEN IT RUNS, from the then-current values.
// Resolves the FIRST outcome (a flush point waiting for it needs one to count); every
// changed path gets its own outcome in the burst, so the card lists each by label.
function runDecisionSave (rec, burst, opts) {
  var spec = rec.decision
  var entries = decisionEntries(rec)
  var firstPath = spec ? spec.paths[0] : ''
  var outcome

  entries.forEach(function (e) {
    e.queued = false
    e.queuedPromise = null
  })

  // A save that another path already STARTED (the page-leave keepalive POST) is this
  // task: its outcome is handled once, and no duplicate request goes out.
  var started = entries.filter(function (e) {
    return e.inFlight && e.inFlightPromise
  })[0]
  if (started) {
    return started.inFlightPromise
  }

  var release = function () {
    spec.paths.forEach(function (p) {
      releaseIfIdle(rec, p)
    })
    refreshScope(rec.scope)
  }

  if (!spec || isTearingDown()) {
    outcome = outcomeFor(rec, firstPath, 'teardown')
    burst.results.push(outcome)
    return Promise.resolve(outcome)
  }

  // The values as they are NOW, the bases the user saw, and what differs.
  var values = {}
  var bases = {}
  var changed = []
  spec.paths.forEach(function (p) {
    var e = rec.entries[p]
    values[p] = cloneValue(getPath(rec.row, p))
    bases[p] = e ? cloneValue(e.value) : decisionBase(rec, p, null)
    if (e && !valuesEqual(p, values[p], bases[p])) {
      changed.push(p)
    }
  })

  // Changed and changed back sends nothing.
  if (changed.length === 0) {
    release()
    outcome = outcomeFor(rec, firstPath, 'skipped')
    burst.results.push(outcome)
    return Promise.resolve(outcome)
  }

  if (typeof rec.decisionSender !== 'function') {
    console.error('requestRowSync: no decision sender registered for', rec.queueKey)
    var unsent = changed.map(function (p) {
      var e = rec.entries[p]
      if (e) {
        e.lastOutcome = 'no-sender'
      }
      var o = outcomeFor(rec, p, 'no-sender')
      burst.results.push(o)
      return o
    })
    refreshScope(rec.scope)
    return Promise.resolve(unsent[0])
  }

  var req = {
    rec: rec,
    kind: 'decision',
    decision: spec,
    values: values,
    bases: bases,
    changed: changed,
    seq: nextSeq(),
    burst: burst,
    keepalive: !!(opts && opts.keepalive)
  }

  entries.forEach(function (e) {
    e.inFlight = true
    e.inFlightSeq = req.seq
    e.inFlightValue = cloneValue(values[e.path])
    e.sentChanged = changed.indexOf(e.path) !== -1
  })
  refreshScope(rec.scope)
  bump()

  var started2
  if (req.keepalive) {
    try {
      started2 = Promise.resolve(rec.decisionSender(req))
    } catch (e) {
      started2 = Promise.reject(e)
    }
  } else {
    started2 = Promise.resolve().then(function () {
      return rec.decisionSender(req)
    })
  }

  var inFlight = started2.then(function (result) {
    return result
  }, function (err) {
    console.error('requestRowSync: decision sender threw', rec.queueKey, err)
    return { status: 'error', err: err }
  }).then(function (result) {
    var res = result || { status: 'failed' }
    var outcomes = changed.map(function (p) {
      var o = outcomeFor(rec, p, res.status)
      Object.keys(res).forEach(function (k) {
        if (k !== 'status' && k !== 'values' && k !== 'serverValues' && k !== 'stashIds') {
          o[k] = res[k]
        }
      })
      o.path = p
      if (res.stashIds && res.stashIds[p] !== undefined) {
        o.stashId = res.stashIds[p]
      }
      o.value = res.values && res.values[p] !== undefined ? res.values[p] : values[p]
      if (res.serverValues && res.serverValues[p] !== undefined) {
        o.serverValue = res.serverValues[p]
      }
      return o
    })
    settleDecisionOutcome(rec, req, res)
    outcomes.forEach(function (o) {
      burst.results.push(o)
    })
    refreshScope(rec.scope)
    bump()
    return outcomes[0]
  })
  // A flush that meets this group mid-flight awaits it and counts its outcome.
  entries.forEach(function (e) {
    e.inFlightPromise = inFlight
  })
  return inFlight
}

// What the group's entries do after the sender answered. The sender has already
// adopted whatever rows came back and (for a refusal) taken the server's values.
function settleDecisionOutcome (rec, req, res) {
  var spec = rec.decision

  spec.paths.forEach(function (path) {
    var entry = rec.entries[path]
    if (!entry) {
      return
    }
    // Did the user change this path again while the save was in flight?
    var untouched = valuesEqual(path, getPath(rec.row, path), entry.inFlightValue)
    entry.inFlight = false
    entry.inFlightSeq = null
    entry.inFlightValue = undefined
    entry.inFlightPromise = null
    entry.sentChanged = false
    entry.lastOutcome = res.status
    if (res.status === 'saved') {
      // The response is the truth (D5): the base becomes the SERVER's value for the
      // path, not the value that was sent (a status-only send comes back with
      // today's date). A path the user did not touch since shows it; one they changed
      // again stays as they left it, dirty against the new base, for the next pause.
      var server = decisionBase(rec, path, path)
      if (server === undefined || (rec.workflow[path] === undefined && path !== LEGAL_PATH)) {
        server = cloneValue(req.values[path])
      }
      entry.value = cloneValue(server)
      entry.networkFailed = false
      entry.cue = null
      delete rec.deferred[path]
      if (untouched) {
        assignPath(rec.row, path, cloneValue(server))
      }
    } else if (res.status === 'network') {
      entry.networkFailed = true
    } else if (res.status === 'blocked') {
      // The control must not keep a value that will never be sent.
      takeServer(rec, path)
    }
  })

  // The group is released as a unit, once EVERY entry has settled (a group is held
  // while any of its paths is in flight).
  if (res.status === 'saved') {
    spec.paths.forEach(function (path) {
      releaseIfIdle(rec, path)
    })
  }
}

// A field changed while its own save was in flight is dirty again the moment
// that save lands. A flush POINT (leaving the page, a command, a close) must not
// return before it is saved too, so it runs further passes, up to this many. The
// timer's pause-flush does not: the user is still working, and that change has
// restarted the pause.
var MAX_FLUSH_PASSES = 3

// Only a field whose last save SUCCEEDED qualifies: a 409, a network failure or
// a refusal is never retried inside the same flush.
function redirtied (recs) {
  return recs.some(function (rec) {
    return Object.keys(rec.entries).some(function (path) {
      var entry = rec.entries[path]
      return entry.lastOutcome === 'saved' && !entry.inFlight && !entry.queued && isDirty(rec, path)
    })
  })
}

// The shared worker behind every flush. For each held field of the records:
// dirty -> queue one save on its row; clean -> release it (changed back or
// never changed), which applies a parked server value. All passes of one flush
// share one burst, so the popup is one.
function flushRecords (recs, opts) {
  opts = opts || {}
  if (isTearingDown()) {
    return Promise.resolve({ ok: true, results: [], failed: [], burst: null })
  }
  var burst = null
  var settleAll = opts.reason !== 'pause'
  // Saves of THIS flush's fields that another flush started (in flight or
  // queued): their outcomes count here too, so a flush point is never blind to a
  // failure it merely waited for.
  var externals = []

  function pass (n) {
    var waits = []
    var getBurst = function () {
      if (!burst) {
        burst = newBurst({ scope: opts.scope, reason: opts.reason, system: opts.system, silent: opts.silent, ordering: opts.ordering })
      }
      return burst
    }
    // Phase 1: the content fields of every record. A decision path waits for phase 2.
    recs.forEach(function (rec) {
      Object.keys(rec.entries).forEach(function (path) {
        var entry = rec.entries[path]
        if (!entry) {
          return
        }
        if (decisionSpecFor(rec, path)) {
          return
        }
        if (opts.paths && opts.paths.indexOf(path) === -1) {
          return
        }
        collectPending(entry, externals)
        if (!isDirty(rec, path)) {
          if (!entry.focused && !entry.inFlight && !entry.queued) {
            releaseIfIdle(rec, path)
          }
          return
        }
        if (versionBlocks()) {
          return
        }
        waits.push(enqueueFieldSave(rec, path, getBurst(), opts))
      })
    })
    // Phase 2 (D3, D4): ONE approval command per decision group with a changed path,
    // queued AFTER every content save of phase 1, so the remarks, Revise TP and
    // Re-survey typed in the same pause reach the wire first (a Pending pick clears
    // those flags server-side: sent first, the content PUT would conflict with the
    // user's own decision).
    recs.forEach(function (rec) {
      if (!rec.decision) {
        return
      }
      if (opts.paths && !rec.decision.paths.some(function (p) { return opts.paths.indexOf(p) !== -1 })) {
        return
      }
      decisionEntries(rec).forEach(function (entry) {
        collectPending(entry, externals)
      })
      if (!decisionDirty(rec)) {
        if (!decisionBusy(rec) && !decisionEntries(rec).some(function (e) { return e.focused })) {
          rec.decision.paths.forEach(function (p) {
            releaseIfIdle(rec, p)
          })
        }
        return
      }
      if (versionBlocks()) {
        return
      }
      waits.push(enqueueDecisionSave(rec, getBurst(), opts))
    })
    if (opts.scope) {
      refreshScope(opts.scope)
    }
    bump()
    return Promise.all(waits.concat(externals)).then(function () {
      if (settleAll && waits.length > 0 && n < MAX_FLUSH_PASSES && !isTearingDown() && redirtied(recs)) {
        return pass(n + 1)
      }
    })
  }

  return pass(1).then(function () {
    return flushResult(burst, externals)
  })
}

// The in-flight and queued saves of a field, started by another flush.
function collectPending (entry, out) {
  if (entry.inFlightPromise && out.indexOf(entry.inFlightPromise) === -1) {
    out.push(entry.inFlightPromise)
  }
  if (entry.queued && entry.queuedPromise && out.indexOf(entry.queuedPromise) === -1) {
    out.push(entry.queuedPromise)
  }
}

// The flush's result: this burst's outcomes plus those of the saves it waited
// for. Only the burst's own outcomes are reported (the others belong to the
// burst that started them).
function flushResult (burst, externals) {
  var summary = burst ? finishBurst(burst) : summariseBurst([])
  var results = burst ? burst.results.slice() : []
  return Promise.all(externals).then(function (outcomes) {
    outcomes.forEach(function (o) {
      if (o && results.indexOf(o) === -1) {
        results.push(o)
      }
    })
    prune()
    var all = summariseBurst(results)
    return { ok: all.ok && summary.ok, results: results, failed: all.failed, burst: burst }
  })
}

// After a 428 nothing can be saved: the page is out of date.
function versionBlocks () {
  return syncState.versionRequiredSeen
}

/**
 * Queue one save per dirty path in `items` ([{row, path}]) as ONE burst. For
 * EditPane Save (ActionDetails, ProgressStep) and system saves.
 *
 * @param {Array}  items
 * @param {Object} opts  { reason, system, silent }
 * @return {Promise<{ok, results, failed, burst}>}
 */
export function flushPaths (items, opts) {
  opts = opts || {}
  var burst = null
  var waits = []
  var externals = []
  items.forEach(function (item) {
    var rec = recordFor(item.row, item.kind)
    if (!rec || !rec.entries[item.path]) {
      return
    }
    collectPending(rec.entries[item.path], externals)
    if (!isDirty(rec, item.path)) {
      releaseIfIdle(rec, item.path)
      return
    }
    if (isTearingDown() || versionBlocks()) {
      return
    }
    if (!burst) {
      burst = newBurst({ scope: rec.scope, reason: opts.reason || 'save', system: opts.system, silent: opts.silent, ordering: opts.ordering })
    }
    waits.push(enqueueFieldSave(rec, item.path, burst, opts))
  })
  bump()
  refreshActivity()
  return Promise.all(waits.concat(externals)).then(function () {
    return flushResult(burst, externals)
  })
}

/**
 * Flush the given rows now (a flush point: a command on those rows). Cancels
 * nothing; the form timer, if running, finds nothing dirty afterwards.
 */
export function flushRows (rows, opts) {
  var recs = []
  ;(rows || []).forEach(function (row) {
    var rec = getRecord(row)
    if (rec && recs.indexOf(rec) === -1) {
      recs.push(rec)
    }
  })
  return flushRecords(recs, opts)
}

/* ------------------------------------------------------------------------
   Scopes and the form-level pause (R2.23)
------------------------------------------------------------------------ */

/**
 * Create a form scope: one per request drawer (RequestWorkflow and everything
 * inside it). Rows registered with `{scope}` belong to it; any change in it
 * restarts its one timer.
 *
 * `scope.state` is reactive (the aggregate of every scope is `syncState.activity`,
 * which the C-12 popup reads):
 *   pending  changed fields are waiting for the pause, nothing in flight
 *   saving   a flush is queued or in flight
 *   unsaved  count of fields whose save failed on the network (still dirty)
 *   pauseStartedAt  when the pause last restarted (restartPause)
 */
// XOL-586 delta 2: a scope's edit-session id (sent as `X-Edit-Session` with its rows'
// saves). Opaque, unique per scope, `[A-Za-z0-9-]` only.
function newEditSessionId () {
  if (typeof crypto !== 'undefined' && crypto && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'es-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12) + '-' + (scopeCounter + 1)
}

export function createScope (name, opts) {
  opts = opts || {}
  var scope = {
    id: ++scopeCounter,
    editSession: newEditSessionId(),
    name: name || 'form',
    pauseMs: typeof opts.pauseMs === 'number' ? opts.pauseMs : PAUSE_MS,
    timer: null,
    rows: [],
    destroyed: false,
    root: null,
    state: Vue.observable({ pending: false, saving: false, unsaved: 0, pauseStartedAt: 0, pauseMs: typeof opts.pauseMs === 'number' ? opts.pauseMs : PAUSE_MS })
  }
  scopes.push(scope)
  return scope
}

// An outcome that neither saves a field nor drops it: the row had no sender, or the
// sender settled `failed` / `error` without taking the server's value. The field stays
// dirty and is not coming back by itself, so the card counts it as NOT SAVED instead of
// letting it sit on "Changes pending" forever (the reviewer's note on C-12).
var UNRESOLVED_OUTCOMES = ['no-sender', 'failed', 'error']

function isUnsavedEntry (entry) {
  return !!entry.networkFailed || UNRESOLVED_OUTCOMES.indexOf(entry.lastOutcome) !== -1
}

/** Recompute `scope.state` from its rows, then the app-wide activity. */
function refreshScope (scope) {
  if (scope) {
    var pending = false
    var saving = false
    var unsaved = 0
    scope.rows.forEach(function (rec) {
      Object.keys(rec.entries).forEach(function (path) {
        var entry = rec.entries[path]
        if (entry.inFlight || entry.queued) {
          saving = true
        } else if (!rec.draft && isDirty(rec, path)) {
          if (isUnsavedEntry(entry)) {
            unsaved++
          } else {
            pending = true
          }
        }
      })
    })
    scope.state.pending = pending
    scope.state.saving = saving
    scope.state.unsaved = unsaved
  }
  refreshActivity()
}

// The aggregate behind the C-12 popup. Reads every registered record (rows outside
// any scope included: an EditPane Save has none), skips open EditPane drafts for
// pending / unsaved, and takes the pause start of the latest pending scope.
function refreshActivity () {
  var pending = false
  var saving = false
  var savingScoped = false
  var unsaved = 0
  allRecords.forEach(function (rec) {
    Object.keys(rec.entries).forEach(function (path) {
      var entry = rec.entries[path]
      if (!entry) {
        return
      }
      if (entry.inFlight || entry.queued) {
        saving = true
        if (rec.scope) {
          savingScoped = true
        }
      } else if (!rec.draft && isDirty(rec, path)) {
        if (isUnsavedEntry(entry)) {
          unsaved++
        } else {
          pending = true
        }
      }
    })
  })
  var started = 0
  var ms = PAUSE_MS
  scopes.forEach(function (scope) {
    if (!scope.destroyed && scope.state.pending && scope.state.pauseStartedAt >= started) {
      started = scope.state.pauseStartedAt
      ms = scope.pauseMs
    }
  })
  // XOL-67 (B4): auto-saves that are not registered rows (the RTA's, lib/rtaAutosave) report here.
  Object.keys(externalActivity).forEach(function (key) {
    var e = externalActivity[key]
    if (e.saving) {
      saving = true
      savingScoped = true
    } else if (e.pending) {
      pending = true
      if (e.pauseStartedAt >= started) {
        started = e.pauseStartedAt
        ms = e.pauseMs
      }
    }
  })
  var a = syncState.activity
  a.pending = pending
  a.saving = saving
  a.savingScoped = savingScoped
  a.unsaved = unsaved
  a.pauseStartedAt = pending ? started : 0
  a.pauseMs = ms
}

// XOL-67 (B4): the save cycle of a save that is NOT a registered row. The RTA's auto-saves (a client's
// response, later the Tower Co decision and Step 5; lib/rtaAutosave) have no field entries and no scope,
// but their "Changes pending", "Saving…" and countdown belong on the same C-12 card. Each reports under
// its own key: { pending, saving, pauseStartedAt, pauseMs }; an idle report removes the key.
var externalActivity = {}

export function setExternalActivity (key, report, keepalive) {
  if (report && (report.pending || report.saving)) {
    externalActivity[key] = {
      pending: !!report.pending,
      saving: !!report.saving,
      pauseStartedAt: report.pauseStartedAt || 0,
      pauseMs: typeof report.pauseMs === 'number' ? report.pauseMs : PAUSE_MS,
      // Starts what is pending as a page-leave (keepalive) request; see startKeepaliveSaves.
      keepalive: typeof keepalive === 'function' ? keepalive : null,
      // The form also wants a page-leave request while its save is in flight (XOL-67 B5: lib/rtaAutosave `keepaliveWhileSaving`).
      keepaliveSaving: !!report.keepaliveSaving
    }
  } else {
    delete externalActivity[key]
  }
  refreshActivity()
}

/**
 * Restart the scope's 3 s timer. Called by `fieldChanged` and by the native
 * keydown / input listener on the scope root (typing in a TextInput, which only
 * emits to its parent on blur). Focus and blur alone never restart it.
 */
export function restartPause (scope) {
  if (!scope || scope.destroyed) {
    return
  }
  if (scope.timer) {
    clearTimeout(scope.timer)
  }
  scope.state.pauseStartedAt = Date.now()
  refreshActivity()
  scope.timer = setTimeout(function () {
    scope.timer = null
    flushScope(scope, { reason: 'pause' })
  }, scope.pauseMs)
}

export function cancelPause (scope) {
  if (scope && scope.timer) {
    clearTimeout(scope.timer)
    scope.timer = null
  }
}

/**
 * Listen for native `keydown` and `input` on the scope's root element, with
 * capture, and restart the pause on each. One listener pair per scope.
 */
export function attachScopeRoot (scope, el) {
  detachScopeRoot(scope)
  if (!scope || !el || typeof el.addEventListener !== 'function') {
    return
  }
  var handler = function () {
    restartPause(scope)
  }
  el.addEventListener('keydown', handler, true)
  el.addEventListener('input', handler, true)
  scope.root = { el: el, handler: handler }
}

export function detachScopeRoot (scope) {
  if (scope && scope.root) {
    scope.root.el.removeEventListener('keydown', scope.root.handler, true)
    scope.root.el.removeEventListener('input', scope.root.handler, true)
    scope.root = null
  }
}

/**
 * Pause-flush: cancel the timer and, for every held field in the scope, queue
 * one save of each field that differs from its base value (a field changed and
 * changed back sends nothing). One call is one burst, so one popup. Also the
 * flush point for drawer close, navigation, commands and Save.
 *
 * @param {Object} scope
 * @param {Object} opts  { reason, system, silent }
 * @return {Promise<{ok, results, failed, burst}>}
 */
export function flushScope (scope, opts) {
  opts = opts || {}
  cancelPause(scope)
  var merged = {
    scope: scope,
    reason: opts.reason || 'flush',
    system: opts.system,
    silent: opts.silent,
    ordering: opts.ordering,
    paths: opts.paths
  }
  return flushRecords(scope.rows.slice(), merged)
}

/**
 * Drawer close (R2.14): detach the root listener, stop the timer, run the
 * pause-flush now, and release the scope once its rows are clean. A field whose
 * save failed on the network keeps its record, so it stays pending.
 *
 * A record that ANOTHER live scope also lists (two drawers on the same row
 * object) is not released: it moves to that scope, so closing the newer drawer
 * does not leave the older one's rows on a dead scope.
 */
export function destroyScope (scope) {
  if (!scope) {
    return Promise.resolve({ ok: true, results: [], failed: [], burst: null })
  }
  detachScopeRoot(scope)
  cancelPause(scope)
  var p = flushScope(scope, { reason: 'close' })
  scope.destroyed = true
  scope.rows.forEach(function (rec) {
    var other = liveScopeListing(rec, scope)
    if (other) {
      if (rec.scope === scope) {
        rec.scope = other
      }
      refreshScope(other)
    } else {
      rec.released = true
    }
  })
  return p.then(function (res) {
    prune()
    return res
  })
}

// Another scope that is still open and lists the record, or null.
function liveScopeListing (rec, except) {
  for (var i = 0; i < scopes.length; i++) {
    if (scopes[i] !== except && !scopes[i].destroyed && scopes[i].rows.indexOf(rec) !== -1) {
      return scopes[i]
    }
  }
  return null
}

/* ------------------------------------------------------------------------
   Leaving with saves pending (R2.18): the library half
------------------------------------------------------------------------ */

/**
 * Is anything left to save? (`toPath`: the fullPath of the navigation being asked
 * about, so a leave bypass for THAT navigation can answer false.) A held field that is dirty (including one waiting
 * for the pause), a queued save, or one in flight. A field whose save failed
 * with 409 / 422 / request_closed is not pending (the server value was taken).
 * Nothing is pending during a session teardown, and nothing can be saved once a
 * 428 said the page is out of date.
 *
 * With a `scope`, only that drawer's rows count: closing one drawer is not held
 * up by another drawer's fields.
 */
export function hasPendingSaves (scope, toPath) {
  if (isTearingDown() || syncState.versionRequiredSeen || isBypassed(toPath)) {
    return false
  }
  var recs = scope ? scope.rows : allRecords
  for (var i = 0; i < recs.length; i++) {
    var rec = recs[i]
    var paths = Object.keys(rec.entries)
    for (var j = 0; j < paths.length; j++) {
      var entry = rec.entries[paths[j]]
      if (isPendingEntry(rec, entry, paths[j])) {
        return true
      }
    }
  }
  return false
}

// A request drawer that guards its own close (LayerHost leave guard) marks its
// scope `guarded`; the global navigation guard leaves those rows to it, so a close
// is never guarded twice. A destroyed scope guards nothing.
function isGuardedRec (rec) {
  return !!rec.scope && !rec.scope.destroyed && !!rec.scope.guarded
}

export function setScopeGuarded (scope, on) {
  if (scope) {
    scope.guarded = !!on
  }
}

/**
 * Is anything left to save OUTSIDE the drawers that guard their own close (a
 * request drawer in a popup, a field whose save failed on the network after its
 * drawer closed)? The synchronous "needed?" check of the global navigation guard:
 * nothing pending means the navigation is not touched. Drafts are not pending.
 */
export function hasUnguardedPendingSaves (toPath) {
  if (isTearingDown() || syncState.versionRequiredSeen || isBypassed(toPath)) {
    return false
  }
  for (var i = 0; i < allRecords.length; i++) {
    var rec = allRecords[i]
    if (isGuardedRec(rec)) {
      continue
    }
    var paths = Object.keys(rec.entries)
    for (var j = 0; j < paths.length; j++) {
      if (isPendingEntry(rec, rec.entries[paths[j]], paths[j])) {
        return true
      }
    }
  }
  return false
}

// The bypass belongs to ONE navigation: it applies only when the caller says which
// navigation it is asking about (`toPath`, the route's fullPath) and that is the one
// the user gave up their saves for. An aborted "Leave now" navigation, or a second
// navigation or drawer close to somewhere else, still flushes.
function isBypassed (toPath) {
  return !!leaveBypass && toPath !== undefined && toPath !== null && leaveBypass.to === toPath
}

/**
 * Has the user given up their saves for THIS navigation (Log out anyway, Leave
 * now)? The drawer's approval-lock prompt reads it: a close the user already
 * chose to abandon is not interrupted again.
 */
export function isLeaveBypassed (toPath) {
  return isBypassed(toPath)
}

/**
 * The user chose to leave (Log out anyway, Leave now): the pending saves are given
 * up for THIS navigation (`to` = its fullPath), so no other guard holds it. Cleared
 * by `clearLeaveBypass` once the navigation settles, and when a different
 * navigation starts.
 */
export function setLeaveBypass (to) {
  leaveBypass = { to: to }
}

export function clearLeaveBypass (exceptTo) {
  if (leaveBypass && (exceptTo === undefined || leaveBypass.to !== exceptTo)) {
    leaveBypass = null
  }
}

/**
 * Close of ONE drawer (R2.14, R2.18 A): stop the scope's timer, flush the scope's
 * rows now, and wait for their queued and in-flight saves, and only those.
 * Resolves like flushScope: `{ok, results, failed, burst}`.
 */
export function flushScopeAndWait (scope, opts) {
  opts = opts || {}
  if (!scope) {
    return Promise.resolve({ ok: true, results: [], failed: [], burst: null })
  }
  var keys = scope.rows.map(function (rec) {
    return rec.queueKey
  })
  return flushScope(scope, { reason: opts.reason || 'leave' }).then(function (res) {
    return whenIdle(keys).then(function () {
      return res
    })
  })
}

/**
 * Cancel every form timer, flush every scope (and every scope-less record) now,
 * and wait for every queued and in-flight save. Resolves `{ok, results, failed}`;
 * `ok` is false when any save did not succeed. With `{unguarded: true}` the rows of
 * the drawers that guard their own close are left out. An open EditPane form's
 * draft is never flushed.
 */
export function flushAllAndWait (opts) {
  opts = opts || {}
  if (isTearingDown()) {
    return Promise.resolve({ ok: true, results: [], failed: [] })
  }
  var groups = []
  var loose = []
  var keys = []
  allRecords.forEach(function (rec) {
    // `unguarded`: the drawers that guard their own close are left to their guard.
    if (opts.unguarded && isGuardedRec(rec)) {
      return
    }
    keys.push(rec.queueKey)
    if (rec.scope) {
      if (groups.indexOf(rec.scope) === -1) {
        groups.push(rec.scope)
      }
    } else if (!rec.draft) {
      // An open EditPane form's draft is not flushed by leaving the page.
      loose.push(rec)
    }
  })
  var waits = groups.map(function (scope) {
    cancelPause(scope)
    return flushRecords(allRecords.filter(function (rec) {
      return rec.scope === scope
    }), { scope: scope, reason: opts.reason || 'leave' })
  })
  if (loose.length > 0) {
    waits.push(flushRecords(loose, { reason: opts.reason || 'leave' }))
  }
  return Promise.all(waits).then(function (all) {
    return whenIdle(opts.unguarded ? keys : undefined).then(function () {
      var results = []
      var failed = []
      all.forEach(function (r) {
        results = results.concat(r.results)
        failed = failed.concat(r.failed)
      })
      return { ok: failed.length === 0, results: results, failed: failed }
    })
  })
}

/**
 * "Close without saving" (XOL-596, Carlos 2026-10-08): the user gave up on the
 * pending saves of a drawer (`{scope}`) or of every drawer that does not guard its
 * own close (`{unguarded: true}`). Every pending field takes the last value the
 * server gave us (so the shared row stops showing text that was never saved) and its
 * entry is dropped, so a queued or retrying save finds nothing to send. An open
 * EditPane draft is not touched. Returns the
 * number of fields discarded. A save already in flight is NOT discarded (it cannot
 * be cancelled; it lands and is adopted normally). A pause timer still running finds
 * nothing dirty.
 */
export function discardUnsaved (opts) {
  opts = opts || {}
  var recs = opts.scope ? opts.scope.rows.slice() : allRecords.slice()
  var n = 0
  recs.forEach(function (rec) {
    if (opts.unguarded && isGuardedRec(rec)) {
      return
    }
    Object.keys(rec.entries).forEach(function (path) {
      var entry = rec.entries[path]
      // A save already ON THE WIRE is not discarded: it cannot be taken back, so it
      // finishes and whatever it returns is adopted like any answer. Only the
      // unsent (dirty or queued) entries go.
      if (entry && !entry.inFlight && isPendingEntry(rec, entry, path)) {
        entry.focused = false
        takeServer(rec, path)
        n++
      }
    })
  })
  recs.forEach(function (rec) {
    refreshScope(rec.scope)
  })
  bump()
  return n
}

/* ------------------------------------------------------------------------
   Browser reload / tab close (R2.18 B): the library half
------------------------------------------------------------------------ */

// A page may have about 64 KB of keepalive request bodies in flight; one path per
// request keeps each small, and a body that would not fit is not started.
export var KEEPALIVE_BUDGET = 60000

function bodyBytes (body) {
  var text = JSON.stringify(body)
  try {
    return new Blob([text]).size
  } catch (e) {
    return text.length * 3
  }
}

/**
 * Anything the user would lose by leaving the page now: a pending save (dirty,
 * queued or in flight), a save that failed on the network, an open EditPane
 * form's draft, and a workflow command (Lock, Complete, Reject, ...) sent and not
 * answered yet: its answer is what the screen waits for, and a reload would drop it
 * (the request may or may not have committed). Decides whether the browser's leave
 * prompt is shown.
 */
export function hasUnsavedWork () {
  if (isTearingDown()) {
    return false
  }
  return pendingFields({ drafts: true }).length > 0 || commandsInFlight() || Object.keys(externalActivity).length > 0
}

function commandsInFlight () {
  return allRecords.some(function (rec) {
    return rec.commandsInFlight > 0
  })
}

/**
 * START every pending field save now, as `keepalive` requests, so they can outlive
 * the page (beforeunload / pagehide cannot await). Each one goes through the
 * record's own sender (`req.keepalive`), which builds the same v3 body with the
 * same per-field `base` and handles the answer like any save. A field already in
 * flight is not started again, and the queue treats the started save as that
 * field's task, so no axios duplicate follows. A draft is never started. Does
 * nothing during a teardown or after a 428. Returns the number started.
 */
export function startKeepaliveSaves () {
  if (isTearingDown() || syncState.versionRequiredSeen) {
    return 0
  }
  var burst = null
  var waits = []
  var budget = KEEPALIVE_BUDGET
  allRecords.forEach(function (rec) {
    if (rec.draft) {
      return
    }
    Object.keys(rec.entries).forEach(function (path) {
      var entry = rec.entries[path]
      if (!entry || entry.inFlight || !isDirty(rec, path) || typeof rec.sender !== 'function') {
        return
      }
      var body = buildFieldBody(rec, path, getPath(rec.row, path), entry)
      if (!body) {
        return
      }
      var size = bodyBytes(body)
      if (size > budget) {
        return
      }
      budget -= size
      if (!burst) {
        burst = newBurst({ reason: 'unload' })
      }
      waits.push(runFieldSave(rec, path, burst, { keepalive: true }))
    })
  })
  // A pending decision group goes out as ONE approval POST (D15), counted against the
  // same byte budget by the size of what it sends. The record's decision sender builds
  // the body; a group already in flight is not started again.
  allRecords.forEach(function (rec) {
    if (rec.draft || !rec.decision || typeof rec.decisionSender !== 'function' || !decisionDirty(rec) || decisionBusy(rec)) {
      return
    }
    var values = {}
    rec.decision.paths.forEach(function (p) {
      values[p] = getPath(rec.row, p)
    })
    var size = bodyBytes({ values: values, expected: rec.decision.paths.map(function (p) { return rec.entries[p] ? rec.entries[p].value : null }) })
    if (size > budget) {
      return
    }
    budget -= size
    if (!burst) {
      burst = newBurst({ reason: 'unload' })
    }
    waits.push(runDecisionSave(rec, burst, { keepalive: true }))
  })
  if (burst) {
    Promise.all(waits).then(function () {
      finishBurst(burst)
    })
  }
  // XOL-67 (B4): an RTA auto-save that is waiting for its pause goes out too (lib/rtaAutosave).
  var external = 0
  Object.keys(externalActivity).forEach(function (key) {
    var e = externalActivity[key]
    if (e && e.keepalive && ((e.pending && !e.saving) || (e.saving && e.keepaliveSaving))) {
      e.keepalive()
      external++
    }
  })
  return waits.length + external
}

/* ------------------------------------------------------------------------
   The stash: what the user typed that was not saved (C-1)
------------------------------------------------------------------------ */

/**
 * Session-only list of values a refused save lost. The notices, the logout
 * dialog and the 428 prompt list them. Cleared by the reset hook.
 */
export function stashLost (item) {
  var entry = {}
  Object.keys(item || {}).forEach(function (k) {
    entry[k] = item[k]
  })
  entry.stashId = ++stashCounter
  stash.push(entry)
  bump()
  return entry
}

export function getStash () {
  return stash.slice()
}

/** Remove the given stash ids (a dismissed notice), or all when none given. */
export function clearStash (ids) {
  if (!ids) {
    stash = []
  } else {
    stash = stash.filter(function (e) {
      return ids.indexOf(e.stashId) === -1
    })
  }
  bump()
}

/* ------------------------------------------------------------------------
   428 version_required (B3)
------------------------------------------------------------------------ */

/**
 * Register the reload prompt (slice 2). Called with (self, ctx, {first}).
 * Survives resetSyncState.
 */
export function setReloadPrompter (fn) {
  reloadPrompter = typeof fn === 'function' ? fn : null
}

/**
 * Called by PostMixins.send on a 428 `version_required`. Marks the page out of
 * date (reactive, the C-8 banner reads it) and hands over to the prompt.
 * Unsaved edits are neither reverted nor adopted; nothing saves after this.
 */
export function promptReload (self, ctx) {
  var first = !syncState.versionRequiredSeen
  syncState.versionRequiredSeen = true
  if (reloadPrompter) {
    try {
      reloadPrompter(self, ctx, { first: first })
    } catch (e) {
      console.error('requestRowSync: reload prompter threw', e)
    }
  }
}

/* ------------------------------------------------------------------------
   Reset (XOL-446)
------------------------------------------------------------------------ */

/**
 * Forget everything session-scoped: timers, scopes, records, the queue, the
 * stash and the 428 flag. Registered with the session teardown below, so a
 * logout (or a forced 401) never leaves one user's unsaved fields for the next.
 * The burst reporter and the reload prompter are app wiring and stay.
 */
export function resetSyncState () {
  scopes.forEach(function (scope) {
    cancelPause(scope)
    detachScopeRoot(scope)
  })
  scopes = []
  allRecords = []
  recordsByRow = new WeakMap()
  queues = {}
  stash = []
  leaveBypass = null
  externalActivity = {}
  syncState.versionRequiredSeen = false
  // The C-12 card's sections, Saved, retry strip and announcements go with it.
  savePopup.reset()
  refreshActivity()
  bump()
}

registerResetHook('requestRowSync', resetSyncState)

export default {
  PAUSE_MS: PAUSE_MS,
  syncState: syncState,
  isValidPath: isValidPath,
  getPath: getPath,
  setPath: setPath,
  valuesEqual: valuesEqual,
  registerRow: registerRow,
  getRecord: getRecord,
  holdField: holdField,
  releaseField: releaseField,
  fieldChanged: fieldChanged,
  fieldCue: fieldCue,
  registerDecision: registerDecision,
  decisionDay: decisionDay,
  adoptRow: adoptRow,
  buildFieldBody: buildFieldBody,
  createScope: createScope,
  flushScope: flushScope,
  hasPendingSaves: hasPendingSaves,
  flushAllAndWait: flushAllAndWait,
  resetSyncState: resetSyncState
}
